import { COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../context/complexDocumentContext';
import { HEADER, FOOTER, BARCODE_ELEMENT, IMAGE_ELEMENT, FILLABLE_ELEMENT, FILLABLE_CHECKBOX_TYPE, FILLABLE_RADIO_BUTTON_GROUP_TYPE,
  OPTIONS_KEY, FIELD_NAME_KEY, GROUP_NAME_KEY, TEXT_ELEMENT, STATIC_TABLE_ELEMENT, PAGE_NUMBER, ELEMENT_TYPES, CREATIVE_BLOCK, DYNAMIC_TABLE_BLOCK, STANDALONE_BLOCK,
  LINK_BLOCK_CONTENT_TYPES, TABLE_OF_CONTENTS, FILLABLE_TEXT_INPUT_TYPE, FILLABLE_COMBO_BOX_TYPE, TEXT_BLOCK,
  STATIC_TABLE_BLOCK, 
  FILLABLE_RADIO_BUTTON_TYPE,
  FILLABLE_CHECKBOX_GROUP_TYPE,
  FILLABLE_COMBOBOX_GROUP_TYPE, 
  LINK_DISPLAY_TEXT,
  SVG_ELEMENT,
  HTML_BLOCK,
  BUTTON_BLOCK,
  LINK_BLOCK,
  SUPPORTED_ENVELOPE_TYPES,
} from '../../utils/complexDocumentConstants';
import { VARIABLE_OBJECT_TYPES, VARIABLE_TYPE } from '../../utils/constants/appConstants';
import { getTodaysDateWithFormat, deepCopy, stringifyEquals, measurementConversion } from '../../utils/javascript';
import { v4 as uuidv4 } from 'uuid';
import { barChartTypes } from './CreativeBlockDesign/Charts/chartConstants';
import HELPER, { autoTransform, buildBusinessLogicMaps } from '../../utils/helper';
import { staticTocFlow } from '../../utils/constants/staticTocFlow';
import { isElementAccessible } from './accessibilityHelper';
import { getAllBlocksInDocument } from '../../utils/masterTemplateHelper';
import { updateBusinessLogic } from '../../utils/services/businessLogicApi';
import { getPgNoDetailsAtIndex } from './CreativeBlockDesign/PageNumber/pageNumberHelper';

export const IMPORT_REUSABLE_PREFIX = 'DKV IMPORT';

const constructPayloadForBlock = (documentContext, newBlock, persistOnlyStandalone = false, isExport = false, documentStyle) => {
    const blockDetails = newBlock; // Expecting newBlock to be an already deep copied json of the block
    if (blockDetails.blockType === STANDALONE_BLOCK) {
      newBlock.blockDTOList = blockDetails.contents.map((contentUUID, index) => {
        const block = Object.assign({}, documentContext[contentUUID], {
          uuid: contentUUID,
          blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contentUUID], // all blocks are standalone
          orderNumber: index // TODO : Check if order number is already maintained
        });
        if (isExport) {
          block.isStandalone = false;
        }
        return constructPayloadForBlock(documentContext, block, persistOnlyStandalone, isExport, documentStyle);
      });
      delete newBlock.contents;
    }
    if(blockDetails.blockType === DYNAMIC_TABLE_BLOCK) {
      const tableProperties = JSON.parse(JSON.stringify(blockDetails.style.tableProperties));
      newBlock.style = JSON.parse(JSON.stringify(blockDetails.style));
      const childBlocks = [];
      if (tableProperties.topBlock) {
        childBlocks.push(blockDetails.style.tableProperties.topBlock);
        const persistedId = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockDetails.style.tableProperties.topBlock];
        if (persistedId) {
          newBlock.style.tableProperties.topBlock = persistedId;
        }
      }
      if (tableProperties.bottomBlock) {
        childBlocks.push(blockDetails.style.tableProperties.bottomBlock);
        const persistedId = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockDetails.style.tableProperties.bottomBlock];
        if (persistedId) {
          newBlock.style.tableProperties.bottomBlock = persistedId;
        }
      }
      if(childBlocks.length > 0) {
        newBlock.blockDTOList = childBlocks.map(childBlockUUID => {
          const childBlock = Object.assign({}, documentContext[childBlockUUID], {
            uuid: childBlockUUID,
            blockId: persistOnlyStandalone
              ? documentContext[childBlockUUID].isStandalone
                ? documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][childBlockUUID]
                : undefined
              : documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][childBlockUUID]
          });
          if (isExport) {
            childBlock.isStandalone = false;
          }
          return childBlock;
        });
      }else {
        newBlock.blockDTOList =[];
      }
    } else if (blockDetails.blockType === STATIC_TABLE_BLOCK) {
      newBlock = Object.assign({}, blockDetails, {
        contents: blockDetails.contents.map(childElement => {
          let cellContents = documentContext[childElement.uuid];
          if (cellContents) {
            cellContents = Object.assign({}, cellContents);
            delete cellContents.isChild;
            delete cellContents.parentBlockUUID;
            delete cellContents.type;
          }
          return Object.assign({}, childElement, cellContents ? {
            contents: [cellContents]
          } : {});
        }),
        style: Object.assign({}, blockDetails.style, { width: undefined })
      });
    }
    if (newBlock.contents?.length > 0) {
      if (Array.isArray(newBlock.contents)) {
        newBlock.contents = newBlock.contents.map(content => {
          const newContent = Object.assign({}, content);
          delete newContent.uuid;
          return newContent;
        });
      } else {
        newBlock.contents = `${newBlock.contents}`;
      }
    }
    delete newBlock.type;
    return newBlock;
}
export const collapseLineBreaks = (blocks) => {
  const result = [];
  let lastWasLineBreak = false;

  for (const block of blocks) {
    if (block.contentType === "LINE_BREAK") {
      if (!lastWasLineBreak) {
        result.push(block);
        lastWasLineBreak = true;
      }
    } else {
      result.push(block);
      lastWasLineBreak = false;
    }
  }

  // Remove trailing LINE_BREAKs
  while (
    result.length > 0 &&
    result[result.length - 1].contentType === "LINE_BREAK"
  ) {
    const before = result[result.length - 3];
    if (before?.contentType === "LIST_LABEL") break;
    result.pop();
  }

  return result;
}

export const  processAccessibleTextBlock = (newBlock, summary= undefined, isTocFlow= false, orderNum=null, isDocSummary=false) => {
  const contents = newBlock?.contents;
  const accessibleBlocks = [];
  const padding = { left: 20, right: 20, top: 20, bottom: 20 };
  const color = '#00000';
  let i = 0;

  const pushBlock = (type, groupContents) => {
    const tocLink = isTocFlow && !isDocSummary ? orderNum > 0 : false 
    accessibleBlocks.push({
      blockType: TEXT_BLOCK,
      style: { padding },
      isStandalone: newBlock.isStandalone,
      ...(newBlock.isStandalone && {blockId: newBlock.blockId}),
      accessibleType: summary ? summary : tocLink ? 'link' : type,
      ...(type === 'heading' && groupContents[0]?.style.heading.level && {level: groupContents[0].style.heading.level }),

      contents: collapseLineBreaks(groupContents),
    });
  };

  while (i < contents?.length) {

    if (contents[i].style) {
      contents[i].style.color = '#00000';
      contents[i].style.backgroundColor = '#fffff';
    }
    const block = contents[i];

    const fontLevelSize = {
      1:20,
      2:18,
      3: 16
    }

    // Heading (with or without hyperlink)
    if (block.contentType === "PARAGRAPH" && block.style?.heading) {
      const fontSize = fontLevelSize[block.style?.heading.level]
      const groupContents = [
        { ...block, style: { ...block.style, fontWeight: ["bold"], fontSize, color }},
      ];
      while (contents[i + 1]?.contentType === "LINE_BREAK") {
        groupContents.push(contents[++i]);
      }
      pushBlock("heading", groupContents);
      i++;
      continue;
    }

    // Hyperlink-only paragraph
    if (
      block.contentType === "PARAGRAPH" &&
      block.style?.hyperlinkValue &&
      !block.style?.heading
    ) {
      const groupContents = [block];
      while (contents[i + 1]?.contentType === "LINE_BREAK") {
        groupContents.push(contents[++i]);
      }
      pushBlock("link", groupContents);
      i++;
      continue;
    }

    // List label + paragraph + optional line break
    if (
      block.contentType === "LIST_LABEL" &&
      contents[i + 1]?.contentType === "PARAGRAPH"
    ) {
      const groupContents = [];

      while (
        contents[i]?.contentType === "LIST_LABEL" &&
        contents[i + 1]?.contentType === "PARAGRAPH"
      ) {
        groupContents.push(contents[i], contents[i + 1]);
        if (contents[i + 2]?.contentType === "LINE_BREAK") {
          groupContents.push(contents[i + 2]);
          i += 3;
        } else {
          i += 2;
        }
      }

      pushBlock("list", groupContents);
      continue; 
    }

    // List label + line break
    if (
      block.contentType === "LIST_VARIABLE" &&
      contents[i + 1]?.contentType === "LINE_BREAK"
    ) {
      const groupContents = [];

      while (
        contents[i]?.contentType === "LIST_VARIABLE" &&
        contents[i + 1]?.contentType === "LINE_BREAK"
      ) {
        groupContents.push(contents[i], contents[i + 1]);
        i += 2;
      }

      pushBlock("list", groupContents);
      continue;
    }

    // List variable + other than line breaks
    if(block.contentType === "LIST_VARIABLE" && contents[i + 1]?.contentType !== "LINE_BREAK") {
      pushBlock("list", [contents[i]]);
      i++;
      continue;
    }


    // Plain paragraphs with optional line breaks or adjacent paragraphs
    if (block.contentType === "PARAGRAPH") {
      const groupContents = [block];
      while (
        contents[i + 1]?.contentType === "LINE_BREAK" ||(
        contents[i + 1]?.contentType === "PARAGRAPH" &&  !contents[i + 1]?.style?.heading && !contents[i + 1].style?.hyperlinkValue)
      ) {
        groupContents.push(contents[++i]);
      }
      pushBlock( "paragraph", groupContents);
      i++;
      continue;
    }

    // Move to next if no match
    i++;
  }

  return accessibleBlocks;
}

export const constructAccessibleBlock = (documentContext, blockUUID, isTOCFlow,  orderNumber, isDocSummary=false, isEmail=false) => {
  let orderNum = orderNumber;
  const blockFromContext = deepCopy(documentContext[blockUUID]);
  const newBlock = Object.assign({}, blockFromContext, {
    orderNumber: orderNum
  });
  const accessibleBlocks = [];
  if(newBlock.style?.summary){
    const processedBlocks = processAccessibleTextBlock({contents:newBlock.style.summary}, 'blockSummary');
    const parentBlock = Object.assign({}, blockFromContext, {
      blockType:'parent',
      orderNumber : orderNum,
      isStandalone:false,
      style:{},
      blockDTOList: processedBlocks,
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId
    });
    delete parentBlock.contents;
    delete parentBlock.uuid;
    accessibleBlocks.push(parentBlock); 
    ++orderNum;
  }
  if (newBlock.blockType === CREATIVE_BLOCK) {
    // TODO : Rewrite creative block to multiple text blocks
    const processedBlockElements = [];
    newBlock.contents.forEach(element => {
      const hasSummary = element?.style?.summary ? element.style.summary.some(item => {
        const content = item.content?.trim();
        return content && content !== '\n';
      }) : false;
      if (element.contentType !== TEXT_ELEMENT && element.style?.summary && hasSummary) {
        processedBlockElements.push(Object.assign({
          blockType: TEXT_BLOCK,
          accessibleType: element.uiType,
          style: {padding:{ left: 20, right: 20, top: 20, bottom: 20 }}
        }, {
          contents: [...(element?.style?.summary || [])]
        }));
      } else if (!isEmail && element.contentType === TEXT_ELEMENT) {
        const processedTextElement = processAccessibleTextBlock(element);
        processedBlockElements.push(...processedTextElement);
      }
    })
    const parentBlock = Object.assign({}, blockFromContext, {
      blockType:'parent',
      orderNumber: orderNum,
      isStandalone:newBlock.isStandalone,
      style:{},
      blockDTOList: processedBlockElements,
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId
    });
    delete parentBlock.contents;
    delete parentBlock.uuid;
      accessibleBlocks.push(parentBlock); 
  } else if (newBlock.blockType === TEXT_BLOCK || newBlock.blockType === BUTTON_BLOCK || newBlock.blockType === LINK_BLOCK) {
    let processedBlocks = processAccessibleTextBlock(newBlock, undefined, isTOCFlow, orderNum, isDocSummary  );
    if(newBlock.blockType === BUTTON_BLOCK) {
      processedBlocks = [];
    }
    if(newBlock.blockType === LINK_BLOCK) {
      newBlock.contents.forEach(content => {
        if(content.summary && content.summary[0]?.content!=="\n") {
          const processedLinkBlock = processAccessibleTextBlock({ contents: content.summary }, undefined, isTOCFlow, orderNum, isDocSummary);
          if(processedLinkBlock[0]) processedLinkBlock[0].accessibleType = 'link';
          processedBlocks = [...processedBlocks, ...processedLinkBlock];
        }
      });
    }
    const parentBlock = Object.assign({}, blockFromContext, {
      blockType:'parent',
      orderNumber: orderNum,
      isStandalone:newBlock.isStandalone,
      style:{},
      blockDTOList: processedBlocks,
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId
    });
    delete parentBlock.contents;
    delete parentBlock.uuid;
    accessibleBlocks.push(parentBlock); 
  } else if (newBlock.blockType === STANDALONE_BLOCK) {
    const childrenBlocks = newBlock.contents.reduce((acc, childBlock) => {
      const [childAccessibleBlocks, increasedOrder] = constructAccessibleBlock(documentContext, childBlock, false, orderNum);
      orderNum = increasedOrder
      const processedChildBlock = [{...childAccessibleBlocks[0], blockId: newBlock.blockId }];
      return [...acc, ...processedChildBlock];
    }, []);
    accessibleBlocks.push(...childrenBlocks);
  } else if (newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
    if(newBlock.style.tableProperties.showTopBlock && newBlock.style.tableProperties.topBlock){
      const topBlockUUID = newBlock.style.tableProperties.topBlock;
      const [topBlockElements, increasedOrder ] = constructAccessibleBlock(documentContext, topBlockUUID, false, orderNum);
      orderNum = increasedOrder
      accessibleBlocks.push(...topBlockElements);
    }
    const dto = Object.assign({}, newBlock, {
      style: {
        ...newBlock.style,
        tableProperties: {...newBlock.style.tableProperties ,showTopBlock: false, showBottomBlock: false }
      }
    });
    const tableBlock = Object.assign({}, newBlock, {
      blockType:'parent',
      orderNumber : orderNum,
      isStandalone:newBlock.isStandalone,
      style:{},
      blockDTOList: [dto],
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId
    });
    delete tableBlock.contents;
    delete tableBlock.uuid;
    accessibleBlocks.push(tableBlock);
    ++orderNum;
    if(newBlock.style.tableProperties.showBottomBlock && newBlock.style.tableProperties.bottomBlock){
      const bottomBlockUUID = newBlock.style.tableProperties.bottomBlock;
      const [bottomBlockElements, increasedOrder] = constructAccessibleBlock(documentContext, bottomBlockUUID, false, orderNum);
      orderNum = increasedOrder
      accessibleBlocks.push(...bottomBlockElements);
    }
  } else if(newBlock.blockType === STATIC_TABLE_BLOCK) {
    newBlock.contents.forEach(element => {
      element.style ={...element.style, padding:{
        "left": 0,
        "right": 0,
        "top": 0,
        "bottom": 0
    }}
      const cellContent = deepCopy(documentContext[element.uuid]?? null);
      const hasSummary = cellContent && cellContent.style?.summary?.some(item => {
        const content = item.content?.trim();
        return content && content !== '\n';
      });
      if( cellContent && cellContent.contentType === 'text'){
        element.contents = [cellContent];
        const cellDetails = cellContent.contents
        for (let i = 0; i < cellDetails.length; i++) {
          cellDetails[i].style.color = '#000000';
          cellDetails[i].style.backgroundColor = '#fffff';
        }
      } else if(cellContent && cellContent.contentType !== 'text' && hasSummary){
        const textBlock = Object.assign({
          contentType: "text",
          contents: [...cellContent.style?.summary],
          style: {width: 100},
          variableContract: cellContent.variableContract,
        });
        element.contents = [textBlock]
      }
    });
    const parentBlock = Object.assign({}, {
      blockType:'parent',
      orderNumber: orderNum,
      isStandalone:newBlock.isStandalone,
      style:{},
      blockDTOList: [{...newBlock}],
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId,
      variableContract:newBlock.variableContract
    });
    accessibleBlocks.push(parentBlock); 
  } else if(newBlock.blockType === HTML_BLOCK){
    const parentBlock = Object.assign({}, newBlock, { 
      blockType:'parent',
      orderNumber: orderNum,
      isStandalone:true,
      style:{},
      blockDTOList: [Object.assign({}, newBlock, { isStandalone:true, accessibleType:'html'})],
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId
    });
    delete parentBlock.contents;
    delete parentBlock.uuid;
      accessibleBlocks.push(parentBlock); 
  } else if (newBlock.blockType === LINK_BLOCK) {
    const processedBlockElements = [];
    newBlock.contents.map(({summary}) => {
      if(summary){
        processedBlockElements.push(Object.assign({
          blockType: TEXT_BLOCK,
          accessibleType: 'text',
          style: {padding:{ left: 20, right: 20, top: 20, bottom: 20 }}
        }, {
          contents: [...summary]
        }));
      }
    })
    const parentBlock = Object.assign({}, newBlock, {
      blockType:'parent',
      orderNumber: orderNum,
      isStandalone:true,
      style:{},
      blockDTOList: processedBlockElements,
      blockId: !documentContext[blockUUID]?.blockId ? documentContext['backendIds'][blockUUID] : documentContext[blockUUID]?.blockId
    });
    delete parentBlock.contents;
    delete parentBlock.uuid;
    accessibleBlocks.push(parentBlock);
  }
  ++orderNum;
  return [accessibleBlocks, orderNum];
}

export const getElementBySelectorAsync = async (selector, caller) => {
  while ( document.querySelector(selector) === null) {
    await new Promise( resolve =>  requestAnimationFrame(resolve) )
  }
  return document.querySelector(selector);
};

export const formDocumentPayloadFromChanges = (documentContext, changes = [],currentDesign, documentStyle, isEmailTemplate=false) => { 
  // CONTRACT is established here
  const payload = {
    documentId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
    documentName: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
    ...(!isEmailTemplate && {style: {...documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], fontFamily: documentStyle.fontFamily, fontSize: documentStyle.fontSize}}),
  };
  let documentLevelChanges= false;
  let sendAllFlows = false;
  let documentSummaryChanges = false;
    const changedEntities = changes.reduce((map, change) => {
    let changeType = {}; 
    if(change.entity === 'document'){
      documentLevelChanges= true;
    } else if (change.entity === 'documentSummary') {
      documentSummaryChanges = true;
    }else if (change.entity === 'flow') {
      documentLevelChanges= false;
      if (map.get(change[change.entity].uuid)) {
        changeType = map.get(change[change.entity].uuid);
      }    
         changeType.flow = true;
      if (change.op === 'add' || change.op === 'move') { // Change in order number of flows
        sendAllFlows = true;
      } else if (change.op === 'update') {
        changeType.style = true;
        // capture changes required for updating all blocks when email width changes.
        if (change.updateKey === 'width' && isEmailTemplate) {
          changeType.sendAllBlocks = true;
          changeType.block = true;
          const contextFlow = documentContext[change[change.entity].uuid];
          const allBlocks = contextFlow.pages.reduce((acc, pageID) => {
            return [...acc, ...contextFlow[pageID]];
          }, []);
          allBlocks.forEach((blockUUID) => {
            const block = documentContext[blockUUID];
            if (block.blockType === DYNAMIC_TABLE_BLOCK) {
              const updatedChangeType = deepCopy({...changeType, childBlock: true });
              map.set(blockUUID, updatedChangeType);
            } else {
              map.set(blockUUID, changeType);
            }
          })
        }
      } else if (change.op === 'delete') {
        sendAllFlows = true;
        changeType = {
          flow: true,
          flowId: change.deleteId,
          isDeleted: true,
          isStandalone:change.flow?.isStandalone         
        };
      } else if (change.op === 'replace') {
        documentSummaryChanges = true;
        sendAllFlows = true;
        const flowUUIDs = Array.isArray(change[change.entity].uuid) ? change[change.entity].uuid : [change[change.entity].uuid];
        flowUUIDs.forEach(newUUID => {
          let existingChange = map.get(newUUID) || {};
          existingChange = {
             ...existingChange,
             flow: true
          };
          map.set(newUUID, existingChange);
        })
        changeType = {
          flow: true,
          flowId: change.deleteId,
          isDeleted: true,
        }
      }
      map.set(change[change.entity].uuid, changeType);
    } else {
      documentLevelChanges= false;
      if (map.get(change.flowUUID)) changeType = map.get(change.flowUUID);
      changeType.flow = documentContext[change.flowUUID]?.pages !== undefined;
      let blockChange = {};
      changeType[change.entity] = true;
      if (change.op === 'add' || change.op === 'move') { // Any changes made in addition or deletion or move are tracked
        if (change.entity === 'block') {
          changeType.sendAllBlocks = true;
          const blockDetails = documentContext[change[change.entity].uuid];
          if (change.op === 'move' && change.sourceFlowUUID) { // Block move has sourceFlowUUID
            console.log("sourceFlowUUID found in change :: ", change);
            const existingChange = map.get(change.sourceFlowUUID) || {};
            map.set(change.sourceFlowUUID, Object.assign({}, existingChange, {
              flow: true,
              block: true,
              sendAllBlocks: true,
              deleteBlocks: [...(existingChange.deleteBlocks || []), change[change.entity].uuid]
            }));
            blockChange = { blockId: change.blockId, isStandalone: blockDetails.isStandalone };
          } else if (change.value?.childBlock) {
            const existingChange = map.get(blockDetails.parentBlockUUID) || {};
            blockChange = Object.assign({}, existingChange, {
              childBlock: true,
              sendAllBlocks: existingChange?.sendAllBlocks || false, // TODO : Change in future for editing grouped blocks
              deleteBlocks: existingChange?.deleteBlocks || []
            });
          }else if(change.op === 'add' && blockDetails.type === DYNAMIC_TABLE_BLOCK){
            const existingChange = map.get(change.blockId) || {};
            blockChange = Object.assign({}, existingChange, {
              childBlock: true,
              sendAllBlocks: existingChange?.sendAllBlocks || false, 
              deleteBlocks: existingChange?.deleteBlocks || []
            });
          }
        } else if (change.entity === PAGE_NUMBER) {
          sendAllFlows = true;
          documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].forEach(existingFlowUUID => {
            changeType = {
              flow: true,
              [change.entity]: true,
            };
            map.set(existingFlowUUID, changeType);
          });
          return map;
        }
      } else if (change.op === 'delete') {
        if (change.entity === HEADER || change.entity === FOOTER) {
          changeType[change.entity] = change[change.entity].uuid;
          changeType[`${change.entity}IsStandalone`] = change[change.entity]?.isStandalone || false;
        } else {
          changeType.sendAllBlocks = true;
          changeType.deleteBlocks = [...(changeType.deleteBlocks || []), change[change.entity].uuid];
        }
        blockChange = { blockId: change.deleteId, isDeleted: true, isStandalone: change[change.entity].isStandalone || false };
      } else if (change.op === 'update') {
        const blockDetails = documentContext[change[change.entity]?.uuid];
        const isChildBlock = blockDetails?.isChild;
        console.log("update change :: ", change, isChildBlock);
        if (isChildBlock) { // TODO : Integrate updates on the child blocks
          const existingChange = map.get(blockDetails.parentBlockUUID) || {};
          map.set(blockDetails.parentBlockUUID, Object.assign({}, existingChange, {
            childBlock: true,
            blockDTOList: [...(existingChange?.blockDTOList || []), change[change.entity].uuid],
            sendAllBlocks: existingChange?.sendAllBlocks || false,
            deleteBlocks: existingChange?.deleteBlocks || []
          }));
        } else if (change.value?.childBlock) {
          const existingChange = map.get(blockDetails.parentBlockUUID) || {};
          blockChange = Object.assign({}, existingChange, {
            childBlock: true,
            sendAllBlocks: existingChange?.sendAllBlocks || false, // TODO : Change in future for editing grouped blocks
            deleteBlocks: existingChange?.deleteBlocks || []
          });
        }
      } else if (change.op === 'replace') {
        const existingChange = map.get(change.blockId) || {};
        changeType.sendAllBlocks = true;
        changeType.style = true;
        changeType.deleteBlocks = existingChange?.deleteBlocks || change.value?.deleteIds || [];
        changeType.deleteBlocks.forEach(deleteId => {
          map.set(deleteId, {
            blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][deleteId],
          });
        });
        change.value &&
          change.value.blocks &&
          change.value.blocks.forEach(block => {
            map.set(block.uuid, block);
          });
      }
      if (!changeType.flow) {
        console.log("Flow is not recognised!!");
      }
      map.set(change.flowUUID, changeType);
        // Include block as change as well
        map.set(change[change.entity]?.uuid, blockChange);
    }
    return map;
  }, new Map());
  if(!documentLevelChanges){
    payload.flows = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].map(
      (existingFlowUUID, index) => ({
        uuid: existingFlowUUID,
        flowId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][existingFlowUUID],
        orderNumber: index + 1,
        isStandalone: documentContext[existingFlowUUID].isStandalone,
        ...(currentDesign === 'block' && {
          style: {
            fontFamily: documentStyle?.fontFamily,
            fontSize: documentStyle?.fontSize,
            width: isEmailTemplate
              ? documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES].width
              : undefined,
          },
        }),
      }),
    ); 
    console.log({ changedEntities });
    if (!sendAllFlows) {
      payload.flows = payload.flows.filter(newFlow => changedEntities.has(newFlow.uuid));
    }
    payload.flows = payload.flows.map((newFlow, index) => {
      const flowUUID = newFlow.uuid;
      const contextFlow = documentContext[flowUUID];
      let flowChanges = {};
      if (changedEntities.has(flowUUID) || Array.from(changedEntities.keys()).some(array => array.includes(flowUUID))) {
        const changesInFlow = changedEntities.get(flowUUID);

        if ((documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][flowUUID] === undefined || (newFlow.isStandalone && currentDesign === 'document')) && currentDesign !== 'block') { // Newly added flow
          const allBlocks = contextFlow.pages.reduce((acc, pageID) => {
            return [...acc, ...contextFlow[pageID]];
          }, []);
          const updatedAllBlocks = contextFlow?.flowName === TABLE_OF_CONTENTS ? [allBlocks[0]] : allBlocks;
          flowChanges = {
            ...newFlow,
            flowName: contextFlow?.flowName,
            style: {
              padding: {
                top: contextFlow.margin[0],
                right: contextFlow.margin[1],
                bottom: contextFlow.margin[2],
                left: contextFlow.margin[3]
              },
              width: contextFlow.width,
              height: contextFlow.height,
              backgroundColor: contextFlow.backgroundColor,
              color: contextFlow.color,
              pageNumber: contextFlow.pageNumber ? {...documentContext[contextFlow.pageNumber] } : undefined,
              ...(contextFlow.isStandalone && {
                fontFamily: contextFlow?.fontFamily,
                fontSize: contextFlow?.fontSize,
              }),
              ...((contextFlow?.flowName === TABLE_OF_CONTENTS) && {fontSize: contextFlow?.fontSize ||20}),              
            },
            // TODO : Remind paper size and orientation should be persisted in backend for front end to know
            paperSize: contextFlow.size,
            paperOrientation: contextFlow.orientation,
            // TODO : Check if standalone block / header is coming here
            header: contextFlow.header ? Object.assign({}, documentContext[contextFlow.header], {
              blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.header]
            }) : undefined,
            footer: contextFlow.footer ? Object.assign({}, documentContext[contextFlow.footer], {
              blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.footer]
            }) : undefined,
            blocks: updatedAllBlocks.map((blockUUID, index) => {
              const blockDetails = documentContext[blockUUID];

              let blockDesign = {
                uuid: blockUUID,
                blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockUUID], // If some blocks have been moved from one flow to another
                orderNumber: index + 1,
                ...blockDetails
              };
              blockDesign = constructPayloadForBlock(documentContext, blockDesign, !newFlow.isStandalone, false, documentStyle);

              delete blockDesign.type;
              return blockDesign;
            })
          };
          let headerChange = flowChanges.header?.isStandalone;
          let footerChange = flowChanges.footer?.isStandalone;
          if (headerChange || footerChange) {
            changesInFlow.header = headerChange;
            changesInFlow.footer = footerChange;
          } else {
            return flowChanges;
          }
        }

        if (changesInFlow && (changesInFlow.style || changesInFlow.pageNumber)) { // Changes in style of flow
          flowChanges = {
            ...flowChanges,
            flowName: contextFlow?.flowName,
            style: {
              padding: {
                top: contextFlow.margin[0],
                right: contextFlow.margin[1],
                bottom: contextFlow.margin[2],
                left: contextFlow.margin[3]
              },
              width: contextFlow.width,
              height: contextFlow.height,
              backgroundColor: contextFlow.backgroundColor,
              color: contextFlow.color,
              pageNumber: contextFlow.pageNumber ?  documentContext[contextFlow.pageNumber] : null,
              ...((currentDesign === 'flow'|| isEmailTemplate) && {fontFamily: documentStyle?.fontFamily, fontSize: documentStyle?.fontSize,}),
              ...((contextFlow?.flowName === TABLE_OF_CONTENTS) && {fontSize: contextFlow?.fontSize ||20}),
            },
            // TODO : Remind paper size and orientation should be persisted in backend for front end to know
            paperSize: contextFlow.size,
            paperOrientation: contextFlow.orientation,
          };
        }

        if (changesInFlow && changesInFlow.header) {
          if (contextFlow.header) {
            let headerContent = deepCopy(documentContext[contextFlow.header]);
            if(headerContent.isStandalone) {
              headerContent = Object.assign({}, headerContent, {
                blockDTOList: [
                  {
                    blockType: headerContent.blockType,
                    contents: headerContent.contents,
                    isDeleted: headerContent.isDeleted,
                    isStandalone: headerContent.isStandalone,
                    isTranslated: headerContent.isTranslated,
                    style:headerContent.style,
                    variableContract: headerContent.variableContract,
                    type: headerContent.type
                  },
                ],
                blockType: STANDALONE_BLOCK,
              });
              delete headerContent.contents;
              delete headerContent.style;
            }
            flowChanges = {
              ...flowChanges,
              header: Object.assign({}, headerContent, {
                blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.header]
              })
            };
            delete flowChanges.header.type;
          } else {
            flowChanges = {
              ...flowChanges,
              header: {
                blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][changesInFlow.header],
                isStandalone: changesInFlow.headerIsStandalone,
                isDeleted: true
              }
            };
          }
        }

        if (changesInFlow && changesInFlow.footer) {
          if (contextFlow.footer) {
            let footerContent = deepCopy(documentContext[contextFlow.footer]);
            if(footerContent.isStandalone) {
              footerContent = Object.assign({}, footerContent, {
                blockDTOList: [
                  {
                    blockType: footerContent.blockType,
                    contents: footerContent.contents,
                    isDeleted: footerContent.isDeleted,
                    isStandalone: footerContent.isStandalone,
                    isTranslated: footerContent.isTranslated,
                    style:footerContent.style,
                    variableContract: footerContent.variableContract,
                    type: footerContent.type
                  },
                ],
                blockType: STANDALONE_BLOCK,
              });
              delete footerContent.contents;
              delete footerContent.style;
            }
            flowChanges = {
              ...flowChanges,
              footer: Object.assign({}, footerContent, {
                blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.footer]
              })
            };
            delete flowChanges.footer.type;
          } else {
            flowChanges = {
              ...flowChanges,
              footer: {
                blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][changesInFlow.footer],
                isStandalone: changesInFlow.footerIsStandalone,
                isDeleted: true
              }
            };
          }
        }

        if (changesInFlow && (changesInFlow.block || changesInFlow.text || changesInFlow.link)) {
          let blocks = [];
          const allBlocks = contextFlow.pages.reduce((acc, pageID) => {
            return [...acc, ...contextFlow[pageID]];
          }, []);
          // Iterate blocks and include order number and block ID mandatorily
          blocks = allBlocks.map((blockUUID, index) => {
            return ({
              uuid: blockUUID,
              blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockUUID],
              isStandalone: documentContext[blockUUID]?.isInteractiveParentBlock ? false : documentContext[blockUUID].isStandalone || false,
              orderNumber: index + 1,
              isEditable: documentContext[blockUUID].isEditable,
            });
          });
          if (!changesInFlow.sendAllBlocks) { // Check if all blocks have to be sent, blocks are rearranged
            blocks = blocks.filter(block => changedEntities.has(block.uuid));
          }
          // Iterate blocks with updates and include only those blocks
          // Check if context content can be pushed directly as contract, means all context content should be equivalent to contract
          blocks = blocks.map(block => {
            const blockChange = changedEntities.get(block.uuid);
            if (blockChange) {
              let changedBlock = Object.assign({}, block, documentContext[block.uuid]);
              if (blockChange.childBlock) {
                if (changedBlock.blockType === DYNAMIC_TABLE_BLOCK) {  // TODO : Handle cases for editing grouped blocks in future
                  let childBlockList = [];
                  if (blockChange?.blockDTOList) {
                    childBlockList = blockChange.blockDTOList;
                    delete changedBlock.contents;
                  } else {
                    if (changedBlock.style.tableProperties.topBlock) {
                      childBlockList.push(changedBlock.style.tableProperties.topBlock);
                    }
                    if (changedBlock.style.tableProperties.bottomBlock) {
                      childBlockList.push(changedBlock.style.tableProperties.bottomBlock);
                    }
                  }
                  if (blockChange.childBlock && childBlockList.length !== 0) {
                    changedBlock.blockDTOList = childBlockList.map(childBlockUUID => {
                      const childBlock = Object.assign({}, documentContext[childBlockUUID]);
                      childBlock.blockId = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][childBlock.uuid];
                      delete childBlock.isChild;
                      delete childBlock.parentBlockUUID;
                      delete childBlock.type;
                      return childBlock;
                    });
                  }
                }
              }
              if (changedBlock.blockType === DYNAMIC_TABLE_BLOCK) {
                changedBlock = Object.assign({}, changedBlock, {
                  style: Object.assign({}, changedBlock.style, {
                    tableProperties: Object.assign({}, changedBlock.style.tableProperties, {
                      topBlock: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][changedBlock.style.tableProperties.topBlock]
                        || changedBlock.style.tableProperties.topBlock,
                      bottomBlock: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][changedBlock.style.tableProperties.bottomBlock]
                        || changedBlock.style.tableProperties.bottomBlock,
                    })
                  })
                });
              }
              if(changedBlock.blockType === STATIC_TABLE_BLOCK) {
                changedBlock = Object.assign({}, changedBlock, {
                  contents: changedBlock.contents.map(childElement => {
                    let cellContents = documentContext[childElement.uuid];
                    if (cellContents) {
                      cellContents = Object.assign({}, cellContents);
                      delete cellContents.isChild;
                      delete cellContents.parentBlockUUID;
                      delete cellContents.type;
                    }
                    return Object.assign({}, childElement, cellContents ? {
                      contents: [cellContents],
                    } : {});
                  }),
                  style: Object.assign({}, changedBlock.style, { width: undefined })
                });
              }
              if (changedBlock.blockType === STANDALONE_BLOCK) {
                  changedBlock = Object.assign({}, changedBlock, {
                    blockDTOList: changedBlock.contents.map(
                      childBlockUUID => {
                        let content = documentContext[childBlockUUID];
                        if (content.blockType === DYNAMIC_TABLE_BLOCK) {
                          let childBlockList = [];
                          
                          if (content.style.tableProperties.topBlock) {
                            childBlockList.push(content.style.tableProperties.topBlock);
                          }
                          if (content.style.tableProperties.bottomBlock) {
                            childBlockList.push(content.style.tableProperties.bottomBlock);
                          }
                      
                          if (childBlockList.length > 0) {
                            content.blockDTOList = childBlockList.map(childBlockUUID => {
                              const childBlock = Object.assign({}, documentContext[childBlockUUID]);
                              childBlock.blockId = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][childBlock.uuid];
                              delete childBlock.isChild;
                              delete childBlock.parentBlockUUID;
                              delete childBlock.type;
                              return childBlock;
                            });
                          }
          
                          content = Object.assign({}, content, {
                            style: Object.assign({}, content.style, {
                              tableProperties: Object.assign({}, content.style.tableProperties, {
                                topBlock: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][content.style.tableProperties.topBlock]
                                  || content.style.tableProperties.topBlock,
                                bottomBlock: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][content.style.tableProperties.bottomBlock]
                                  || content.style.tableProperties.bottomBlock,
                              })
                            })
                          });
                          return content;
                        }
                        if (content.blockType === STATIC_TABLE_BLOCK) {
                          content = Object.assign({}, content, {
                            contents: content.contents.map(childElement => {
                              let cellContents = documentContext[childElement.uuid];
                              if (cellContents) {
                                cellContents = Object.assign({}, cellContents);
                                delete cellContents.isChild;
                                delete cellContents.parentBlockUUID;
                                delete cellContents.type;
                              }
                              return Object.assign({}, childElement, cellContents ? {
                                contents: [cellContents]
                              } : {});
                            }),
                            style: Object.assign({}, content.style, { width: undefined })
                          })
                        }
                        return content;
                      }
                    ),
                  });
                  delete changedBlock.contents;
              }
              console.log('changedBlock :: ', changedBlock, documentContext[block.uuid]);
              delete changedBlock.type;
              return changedBlock;
            }
            return block;
          });
          // Delete blocks that have been persisted and removed
          if (changedEntities.get(flowUUID).deleteBlocks?.length) {
            blocks = [
              ...blocks,
              ...changedEntities.get(flowUUID).deleteBlocks.map(deleteBlock => ({
                uuid: deleteBlock,
                blockId: changedEntities.get(deleteBlock).blockId,
                isDeleted: true,
                isStandalone: changedEntities.get(deleteBlock)?.isStandalone || false,
              })),
            ];
          }

          flowChanges = {
            ...flowChanges,
            blocks
          };
        }
        let flowPayload = {
          ...newFlow,
          ...flowChanges
        };
        console.log({ flowChanges, flowPayload });
        return flowPayload;
      }
      return newFlow;
    });
      changedEntities.forEach((changeDetails, changedEntity) => {
      if (changeDetails.flow && changeDetails.isDeleted) {
        if(Array.isArray(changeDetails.flowId)) {
          changeDetails.flowId.map((id) => {
            payload.flows.push({
              flowId: id,
              isDeleted: true,
              isStandalone: changeDetails.isStandalone,
            });  
          })
        } else {
          payload.flows.push({
            flowId: changeDetails.flowId,
            isDeleted: true,
            isStandalone: changeDetails.isStandalone,
          });
        }
      }
    })  
  }
  if (documentSummaryChanges) {
    payload.summary = Object.assign({}, documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY], {
      blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY_ID]
    });
  }
  return payload;
}

const getDefaultValueForVariable = (variable, globalVariables = []) => {
  if (variable.type === 'ListString') {
    return new Array(5)
              .fill(0)
              .map((a, index) => `${variable.exampleValue} ${index}`);
  } else if (variable.type === 'String') {
    return variable.exampleValue;
  } else if (variable.type === 'Date') {
    return getTodaysDateWithFormat();
  } else if (VARIABLE_OBJECT_TYPES.includes(variable.type)) {
    const objectValue = variable.content.reduce((acc, contentVar) => {
      if (contentVar.type === VARIABLE_TYPE.CHART_OBJECT) {
        const cVarDetails = contentVar.content.reduce((cAcc, cVar) => {
          const cVarDetail = globalVariables.find(gVar => gVar.id === cVar.variableId);
          return { ...cAcc, [cVarDetail.placeHolderText]: getDefaultValueForVariable(cVarDetail) };
        }, {});
        return { ...acc, [contentVar.name]: [cVarDetails] };
      }
      const variableDetails = globalVariables.find(gVar => gVar.id === contentVar.variableId);
      if (variableDetails) {
        return {
          ...acc,
          [variableDetails.placeHolderText]: getDefaultValueForVariable(variableDetails),
        };
      }
      return { ...acc };
    }, {});
    return [objectValue];
  }
}

const updateDataSetFromVariableContract = (
  variableContract,
  dataSet,
  documentVariables,
  globalVariables,
) => {
  variableContract.forEach(blockVariable => {
    const variableName = blockVariable.name || globalVariables.find(gVar => gVar.id === blockVariable.variableId)?.placeHolderText;
    const variableIndex = documentVariables.findIndex(e => blockVariable.variableId ? e.variableId === blockVariable.variableId : e.name === blockVariable.name);
    if (variableIndex === -1) {
      documentVariables.push(deepCopy(blockVariable));
    } else if (VARIABLE_OBJECT_TYPES.includes(blockVariable.type) || VARIABLE_TYPE.TABLE_OBJECT === blockVariable.type) {
      let variableDetails = documentVariables[variableIndex];
      let variablesToAdd = [];
      (blockVariable.content || []).forEach(item => {
        let index = variableDetails.content.findIndex(e => e.placeHolderText === item.name || e.id === item.variableId);
        if (index === -1) {
          variableDetails.content.push(item);
        } else {
          // console.log("nested updateDataSetFromVariableContract :: ", JSON.parse(JSON.stringify(variableDetails.content)), item)
          updateDataSetFromVariableContract([item], dataSet, variableDetails.content, globalVariables);
          // console.log("nested return :: ", variableDetails.content, documentVariables);
        }
      });
      if (variablesToAdd.length > 0) {
        variableDetails.content = [...variableDetails.content, ...variablesToAdd];
      }
      documentVariables = documentVariables.toSpliced(variableIndex, 1, variableDetails);
    }
  });
  console.log("updateDataSetFromVariableContract :: ", variableContract, documentVariables);
};

export const generatePDFPayload = (globalVariables, documentContext, businessLogics, documentStyle, currentDesign, flowUUID = undefined, persistOnlyStandalone = false, isExport = false, extraBlocks = [], isInteractiveDesign= false) => {
  let dataSet = {};
  let documentVariables = [];
  // CONTRACT is established here
  const data = {
    documentId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
    id: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
    name: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
    documentName: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
    resourceGroup:documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.RESOURCE_GROUP_ID],
    style: {...documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], fontFamily: documentStyle.fontFamily, fontSize: documentStyle.fontSize},
  };
  const docSummary = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY];
  const displayName = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DISPLAY_NAME];
  data.flows = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS].map((existingFlowUUID, index) => ({
    uuid: existingFlowUUID,
    flowId: persistOnlyStandalone
      ? documentContext[existingFlowUUID].isStandalone
        ? documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][existingFlowUUID]
        : undefined
      : documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][existingFlowUUID] || "0",
    orderNumber: index + 1,
    isStandalone: isExport ? false : documentContext[existingFlowUUID].isStandalone
  }));
  if (flowUUID !== undefined) {
    data.flows = data.flows.filter(flow => flow.uuid === flowUUID);
  }
  data.flows = data.flows.map((newFlow, index) => {
    const flowUUID = newFlow.uuid;
    const contextFlow = documentContext[flowUUID];

    const allBlocks = contextFlow.pages.reduce((acc, pageID) => {
      return [...acc, ...contextFlow[pageID]];
    }, []);
    const updatedAllBlocks = contextFlow?.flowName === TABLE_OF_CONTENTS ? [allBlocks[0]] : allBlocks;
    const flowPayload = {
      flowName: contextFlow?.flowName,
      ...newFlow,
      style: {
        padding: {
          top: contextFlow.margin[0],
          right: contextFlow.margin[1],
          bottom: contextFlow.margin[2],
          left: contextFlow.margin[3]
        },
        width: contextFlow.width,
        height: contextFlow.height,
        backgroundColor: contextFlow.backgroundColor,
        color: contextFlow.color,
        pageNumber: contextFlow.pageNumber ? {...documentContext[contextFlow.pageNumber]} : undefined,
        ...(currentDesign === 'flow' && {fontFamily: documentStyle?.fontFamily, fontSize: documentStyle?.fontSize,}),
        ...((contextFlow?.flowName === TABLE_OF_CONTENTS) && {fontSize: contextFlow?.fontSize ||20}),
      },
      // TODO : Remind paper size and orientation should be persisted in backend for front end to know
      paperSize: contextFlow.size,
      paperOrientation: contextFlow.orientation,
      header: contextFlow.header ? Object.assign({}, documentContext[contextFlow.header], {
        blockId: persistOnlyStandalone
          ? documentContext[contextFlow.header].isStandalone
            ? documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.header]
            : undefined
          : documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.header]
      }) : undefined,
      footer: contextFlow.footer ? Object.assign({}, documentContext[contextFlow.footer], {
        blockId: persistOnlyStandalone
          ? documentContext[contextFlow.footer].isStandalone
            ? documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.footer]
            : undefined
          : documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][contextFlow.footer]
      }) : undefined,
      blocks: updatedAllBlocks.map((blockUUID, index) => {
        const blockDetails = Object.assign({}, documentContext[blockUUID]);
        const blockDesign = Object.assign({}, {
          uuid: blockUUID,
          blockId: persistOnlyStandalone // If some blocks have been moved from one flow to another
            ? blockDetails.isStandalone
              ? documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockUUID]
              : undefined
            : documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockUUID],
          orderNumber: index + 1,
        }, blockDetails);
        delete blockDesign.type;
        if(isInteractiveDesign) {
          // if ([TEXT_BLOCK, CREATIVE_BLOCK].includes(blockDetails.blockType) && blockDetails?.variableContract?.length && blockDetails?.variableContract[0].type === 'object') {
          //   blockDesign.variableContract =[]
          // }
          // TODO: redundant so remove once autosave is integrated.
          if (!blockDesign.blockId) {
            blockDesign.blockId = uuidv4();
          }
        }
        return blockDesign;
      })
    };
    delete flowPayload.uuid;
    if (flowPayload.header) {
      flowPayload.header = constructPayloadForBlock(documentContext, flowPayload.header, persistOnlyStandalone, isExport, documentStyle);
      if (flowPayload.header.variableContract) {
        updateDataSetFromVariableContract(
          flowPayload.header.variableContract,
          dataSet,
          documentVariables,
          globalVariables,
        );
      }
      if (isExport) {
        flowPayload.header.isStandalone = false;
      } else {
      if(flowPayload.header.isStandalone){
        flowPayload.header = Object.assign({}, flowPayload.header, {
          blockDTOList: [
            {
              blockType: flowPayload.header.blockType,
              contents: flowPayload.header.contents,
              isDeleted: flowPayload.header.isDeleted,
              isStandalone: flowPayload.header.isStandalone,
              isTranslated: flowPayload.header.isTranslated,
              style:flowPayload.header.style,
              variableContract: flowPayload.header.variableContract,
              type: flowPayload.header.type
            },
          ],
          blockType: STANDALONE_BLOCK,
        });
        delete flowPayload.header.contents;
        delete flowPayload.header.style;
      }
      }
      delete flowPayload.header.uuid;
    }
    if (flowPayload.footer) {
      flowPayload.footer = constructPayloadForBlock(documentContext, flowPayload.footer, persistOnlyStandalone, isExport, documentStyle);
      if (flowPayload.footer.variableContract) {
        updateDataSetFromVariableContract(
          flowPayload.footer.variableContract,
          dataSet,
          documentVariables,
          globalVariables,
        );
      }
      if (isExport) {
        flowPayload.footer.isStandalone = false;
      } else {
      if(flowPayload.footer.isStandalone){
        flowPayload.footer = Object.assign({}, flowPayload.footer, {
          blockDTOList: [
            {
              blockType: flowPayload.footer.blockType,
              contents: flowPayload.footer.contents,
              isDeleted: flowPayload.footer.isDeleted,
              isStandalone: flowPayload.footer.isStandalone,
              isTranslated: flowPayload.footer.isTranslated,
              style:flowPayload.footer.style,
              variableContract: flowPayload.footer.variableContract,
              type: flowPayload.footer.type
            },
          ],
          blockType: STANDALONE_BLOCK,
        });
        delete flowPayload.footer.contents;
        delete flowPayload.footer.style;
      }
      }
      delete flowPayload.footer.uuid;
    }
    flowPayload.blocks = flowPayload.blocks.map(block => {
      block = constructPayloadForBlock(documentContext, block, persistOnlyStandalone, isExport, documentStyle);
      if (block.variableContract) {
        updateDataSetFromVariableContract(
          block.variableContract,
          dataSet,
          documentVariables,
          globalVariables,
        );
      }
      delete block.uuid;
      return block;
    });
    return flowPayload;
  });
  if(currentDesign==='document' && docSummary)
  {
   docSummary.contents=docSummary.contents?docSummary.contents:[];
   updateDataSetFromVariableContract(
     docSummary.variableContract,
     dataSet,
     documentVariables,
     globalVariables,
   );
   data.summary = Object.assign({}, docSummary, {
     blockId: documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.DESIGN_SUMMARY_ID]
   })
  }
  if(currentDesign === 'document' && displayName) {
    data.displayNameBlockDTO = displayName;
    if(displayName.variableContract)
      updateDataSetFromVariableContract(
        displayName.variableContract,
        dataSet,
        documentVariables,
        globalVariables,
      );
  }

  const templateInserts = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS];
  const templateBusinessLogics = templateInserts?.map(({businessLogic}) => businessLogic).filter(v => !!v) || [];
  const allBusinessLogics = [...(businessLogics?.length ? businessLogics : []), ...templateBusinessLogics];
  if (allBusinessLogics?.length > 0) {
    allBusinessLogics.forEach(logic => {
      if (logic?.variables?.length > 0) {
        logic.variables.forEach(variable => {
          const selectedVariable = globalVariables?.find(gVar => gVar.id === variable.variableId);
          if (selectedVariable) {
            dataSet[selectedVariable.placeHolderText] = selectedVariable.exampleValue;
          }
          if (!documentVariables.find((dVar) =>  dVar.variableId === variable.variableId)) {
            documentVariables.push(variable);
          }
        });
      }
    });
  }

  if (extraBlocks.length > 0) {
    extraBlocks.forEach(extraBlock => {
      if (extraBlock?.variableContract?.length > 0) {
        updateDataSetFromVariableContract(
          extraBlock.variableContract,
          dataSet,
          documentVariables,
          globalVariables,
        );
      }
    });
  }
  
  data.variableContract = documentVariables;
  data.businessLogics = businessLogics;
  data.language=documentContext.language;
  data.inserts = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS];
  const variableOverrides = documentContext[COMPLEX_DOCUMENT_CONTEXT_KEYS.VARIABLE_OVERRIDES];
  if (variableOverrides && variableOverrides.length) {
    data.dataSetOverride = variableOverrides;
  }
  return ({
    dataSet,
    documentDTO: data
  });
}

export const updateNestedObject = (variableList, localVariableSplit, variableType, dateFormat = undefined, isArray = false) => {
  if (localVariableSplit.length === 1) {
    let localObject = variableList.find(item => item.name === localVariableSplit[0]);
    if (!localObject) {
      variableList.push({
        name: localVariableSplit[0],
        type: variableType === 'ListString' ? 'listOfStrings' : variableType.toLowerCase(),
        dateFormat,
        isArray,
        ...(variableType === 'object' ? { content: [] } : {})
      });
    } else if (isArray && !localObject.isArray) {
      localObject.isArray = true;
    }
    return;
  } else {
    let localObject = variableList.find(item => item.name === localVariableSplit[0]);
    if (!localObject) {
      localObject = {
        name: localVariableSplit[0],
        type: 'object',
        content: []
      };
      variableList.push(localObject);
    }
    updateNestedObject(localObject.content, localVariableSplit.slice(1), variableType, dateFormat, isArray);
  }
}

export const convertMapToList = (variableContract) => {
  let variablesList = [];
  variableContract.forEach((value, key) => {
      if (value.type === VARIABLE_TYPE.CHART_OBJECT) {
        variablesList.push({ name: key, ...value });
      } else if (value.type === FILLABLE_COMBO_BOX_TYPE) {
        variablesList.push({ name: key, variableId: null, type: 'String' });
      } else if (value.type === FILLABLE_CHECKBOX_TYPE) {
        variablesList.push({ name: key, variableId: null, type: 'listOfStrings' });
      } else if (value.type === FILLABLE_RADIO_BUTTON_TYPE) {
        variablesList.push({ name: key, variableId: null, type: 'String' });
      } else if ((value.type === FILLABLE_CHECKBOX_GROUP_TYPE || value.type === FILLABLE_COMBOBOX_GROUP_TYPE || value.type === FILLABLE_RADIO_BUTTON_GROUP_TYPE) || value.type === VARIABLE_TYPE.FORM_OBJECT) {
        variablesList.push({
          name: key,
          type: VARIABLE_TYPE.FORM_OBJECT,
          content: [
            {
              variableId: null,
              name: 'label',
              type: 'string',
            },
            {
              variableId: null,
              name: 'value',
              type: 'string',
            },
            {
              variableId: null,
              name: 'selected',
              type: 'string',
            },
          ],
        });
      } else if (value.type === LINK_DISPLAY_TEXT) {
        variablesList.push({name: key, variableId: null, type: 'String'});
      } else {
        variablesList.push({ variableId: key, ...value });
      }
    });
    return variablesList;
}

export const getCreativeBlockVariableContract = (contents = [], globalVariables, elSummary = [], isRepetitive = false, variableName = '', childBlocks = [], tableProperties = undefined, blockDetails = undefined) => {
  let variableContract = new Map();
  let isTable = tableProperties !== undefined;
  let childBlockContract = new Map();

  const updateLinkDisplayVariable = (el, variableDetails, varContract) => {
    if (el?.style?.hyperlinkValue && el.style.hyperlinkValue.trim() === `{${variableDetails.id}}`) {
      varContract.set(`${variableDetails.placeHolderText}-${LINK_DISPLAY_TEXT}`, {
        type: `${LINK_DISPLAY_TEXT}`,
      });
    }
  };

  const updateCustomLogicContract = el => {
    if (Array.isArray(el?.contents) && el.contents.length > 0) {
      el.contents.forEach(content => {
        if (
          content.content === "custom_logic" &&
          Array.isArray(content.contents) &&
          content.contents.length > 0
        ) {
            content.contents.forEach(cont => {
              if(cont?.style?.isVariable) {
                let variableDetails = globalVariables.filter(
                  globalVariable =>
                    globalVariable.id ===
                    parseInt(cont.content.replaceAll('{', '').replaceAll('}', '')),
                );
                if (variableDetails?.length > 0) {
                  variableDetails = variableDetails[0];
                  variableContract.set(variableDetails.id, {
                    type:
                      variableDetails.type === 'ListString'
                        ? 'listOfStrings'
                        : variableDetails.type.toLowerCase(),
                  });
                }
              }
            })
        }
      });
    }
  };

  const addSummaryContract = summary => {
    if (Array.isArray(summary) && summary.length > 0) {
      summary.forEach(content => {
        if (content.style?.isVariable) {
          let variableDetails = globalVariables.filter(
            globalVariable =>
              globalVariable.id ===
              parseInt(content.content.replaceAll('{', '').replaceAll('}', '')),
          );
          if (variableDetails?.length > 0) {
            variableDetails = variableDetails[0];
            variableContract.set(variableDetails.id, {
              type:
                variableDetails.type === 'ListString'
                  ? 'listOfStrings'
                  : variableDetails.type.toLowerCase(),
            });
            updateLinkDisplayVariable(content, variableDetails, variableContract);
          }
        }
      });
    }
  };

  if (Array.isArray(elSummary) && elSummary.length > 0) {
    addSummaryContract(elSummary);
  }
  // TODO : Calculate variables, Add logic for other elements
  contents.forEach(el => {
    if (el.contentType === BARCODE_ELEMENT) {
      if (el.style.isVariable) {
        let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(el.content));
        if(variableDetails?.length > 0) {
          variableDetails = variableDetails[0];
          variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
        }
      }
      addSummaryContract(el?.style?.summary);
    } else if(el.contentType === SVG_ELEMENT) {
      addSummaryContract(el?.style?.summary);
    } else if (el.contentType === IMAGE_ELEMENT) {
      if (el.style.isVariable) {
        let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(el.content.replaceAll('{', '').replaceAll('}', '')));
        if (variableDetails?.length > 0) {
          variableDetails = variableDetails[0];
          variableContract.set(variableDetails.id, { type: variableDetails.type.toLowerCase()});
        }
      }
      addSummaryContract(el?.style?.summary);
    } else if (el.contentType === FILLABLE_ELEMENT) {
      if (el.fillableType === FILLABLE_CHECKBOX_TYPE) {
        if (el.style.isVariable) {
          variableContract.set(el[OPTIONS_KEY][GROUP_NAME_KEY], { type: FILLABLE_CHECKBOX_TYPE });
        }
      } else if (el.fillableType === FILLABLE_RADIO_BUTTON_TYPE) {
        if (el.style.isVariable) {
          variableContract.set(el[OPTIONS_KEY][GROUP_NAME_KEY], { type: FILLABLE_RADIO_BUTTON_TYPE });
        }
      } else if (el.fillableType === FILLABLE_TEXT_INPUT_TYPE) {
          if(el.contents?.length > 0) {
            el.contents.forEach(content => {
              if(content.style?.isVariable) {
                let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(content.content.replaceAll("{","").replaceAll("}","")));
                if(variableDetails?.length > 0) {
                  variableDetails = variableDetails[0];
                  variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
                  updateLinkDisplayVariable(content, variableDetails, variableContract);
                }
              }
            });
          }
        updateCustomLogicContract(el);
      } else if (el.fillableType === FILLABLE_COMBO_BOX_TYPE) {
        if (el.style.isVariable) {
          variableContract.set(el[OPTIONS_KEY][FIELD_NAME_KEY], { type: FILLABLE_COMBO_BOX_TYPE });
        }
        if (el.contents?.length > 0) {
          el.contents.forEach(contentEl => {
            if (contentEl.content?.length > 0) {
              contentEl.content.forEach(textEl => {
                if (textEl.style?.isVariable) {
                  let variableDetails = globalVariables.filter(
                    globalVariable =>
                      globalVariable.id ===
                      parseInt(textEl.content.replaceAll('{', '').replaceAll('}', '')),
                  );
                  if (variableDetails?.length > 0) {
                    variableDetails = variableDetails[0];
                    variableContract.set(variableDetails.id, {
                      type:
                        variableDetails.type === 'ListString'
                          ? 'listOfStrings'
                          : variableDetails.type.toLowerCase(),
                    });
                    updateLinkDisplayVariable(textEl, variableDetails, variableContract);
                  }
                }
              });
            }
          });
        }
      } else if (el.fillableType === FILLABLE_CHECKBOX_GROUP_TYPE) {
        variableContract.set(el?.contents?.[OPTIONS_KEY][GROUP_NAME_KEY], { type: FILLABLE_CHECKBOX_GROUP_TYPE });
      } else if (el.fillableType === FILLABLE_RADIO_BUTTON_GROUP_TYPE) {
        variableContract.set(el?.contents?.[OPTIONS_KEY][GROUP_NAME_KEY], { type: FILLABLE_RADIO_BUTTON_GROUP_TYPE });
      } else if (el.fillableType === FILLABLE_COMBOBOX_GROUP_TYPE) {
        variableContract.set(el[OPTIONS_KEY][FIELD_NAME_KEY], { type: FILLABLE_COMBOBOX_GROUP_TYPE });
      }
      addSummaryContract(el?.style?.summary);
    } else if (el.contentType === TEXT_ELEMENT || LINK_BLOCK_CONTENT_TYPES.includes(el.contentType)) {
      if(el.contents?.length > 0) {
        el.contents.forEach(content => {
          if(content.style?.isVariable) {
            let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(content.content.replaceAll("{","").replaceAll("}","")));
            if(variableDetails?.length > 0) {
              variableDetails = variableDetails[0];
              variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
              updateLinkDisplayVariable(content, variableDetails, variableContract);
            }
          }
        });
      }
      if(el?.summary) {
        el.summary.forEach(content => {
          if(content.style?.isVariable) {
            let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(content.content.replaceAll("{","").replaceAll("}","")));
            if(variableDetails?.length > 0) {
              variableDetails = variableDetails[0];
              variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
              updateLinkDisplayVariable(content, variableDetails, variableContract);
            }
          }
        });
      }
      updateCustomLogicContract(el);
    } else if (el.contentType === 'tableHeader') {
      isTable = true;
      if (tableProperties.showHeader && el.headerContent.contents.length > 0) {
        el.headerContent.contents.forEach(content => {
          if(content.style?.isVariable) {
            let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(content.content.replaceAll("{","").replaceAll("}","")));
            if(variableDetails?.length > 0) {
              variableDetails = variableDetails[0];
              variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
              updateLinkDisplayVariable(content, variableDetails, variableContract);
            }
          }
        });
        updateCustomLogicContract(el?.headerContent);
      }
      if (tableProperties.showGroupHeaderContent && el.groupHeaderContent?.contents?.length > 0) {
        el.groupHeaderContent.contents.forEach(content => {
          if(content.style?.isVariable) {
            let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(content.content.replaceAll("{","").replaceAll("}","")));
            if(variableDetails?.length > 0) {
              variableDetails = variableDetails[0];
              variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
              updateLinkDisplayVariable(content, variableDetails, variableContract);
            }
          }
        });
        updateCustomLogicContract(el?.groupHeaderContent);
      }
    } else if (el.contentType === STATIC_TABLE_ELEMENT) {
      el.contents.forEach(cell => {
        updateCustomLogicContract(cell);
        cell.contents.forEach(content => {
          if(content.style?.isVariable) {
            let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(content.content.replaceAll("{","").replaceAll("}","")));
            if(variableDetails?.length > 0) {
              variableDetails = variableDetails[0];
              variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
              updateLinkDisplayVariable(content, variableDetails, variableContract);
            }
          }
        });
      });
      addSummaryContract(el?.style?.summary);
    } else if (el.contentType === ELEMENT_TYPES.CHART) {
      const {
        baseVariable,
        labelVariable,
        valueVariable,
        descVariable,
        seriesNameVariable,
        seriesValueVariable,
        type,
      } = el.contents;
      if (barChartTypes.includes(type)) {
        if (el.style?.isVariable && labelVariable && seriesNameVariable && seriesValueVariable) {
          const labelDetails = globalVariables.find(gVar => gVar.id === labelVariable);
          const nameDetails = globalVariables.find(gVar => gVar.id === seriesNameVariable);
          const valueDetails = globalVariables.find(gVar => gVar.id === seriesValueVariable);
          const chartObj = {
            type: VARIABLE_TYPE.CHART_OBJECT,
            content: [
              { variableId: labelVariable, type: labelDetails?.type?.toLowerCase() },
              { variableId: seriesNameVariable, type: nameDetails?.type?.toLowerCase() },
              { variableId: seriesValueVariable, type: valueDetails?.type?.toLowerCase() },
            ],
          };
          variableContract.set(baseVariable, chartObj);
        }
      } else {
        if (el.style?.isVariable && labelVariable && valueVariable && descVariable) {
          const labelDetails = globalVariables.find(gVar => gVar.id === labelVariable);
          const valueDetails = globalVariables.find(gVar => gVar.id === valueVariable);
          const descDetails = globalVariables.find(gVar => gVar.id === descVariable);
          const chartObj = {
            type: VARIABLE_TYPE.CHART_OBJECT,
            content: [
              { variableId: labelVariable, type: labelDetails?.type?.toLowerCase() },
              { variableId: valueVariable, type: valueDetails?.type?.toLowerCase() },
              { variableId: descVariable, type: descDetails?.type?.toLowerCase() },
            ],
          };
          variableContract.set(baseVariable, chartObj);
        }
      }
      addSummaryContract(el?.style?.summary);
    } else if (el.style?.isVariable) {
      let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(el.content.replaceAll("{","").replaceAll("}","")));
      if(variableDetails?.length > 0) {
        variableDetails = variableDetails[0];
        variableContract.set(variableDetails.id, { type: variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase()});
        updateLinkDisplayVariable(el, variableDetails, variableContract);
      }
    } else if (el.contentType === "FUNCTION") {
      if (el.content === 'custom_logic' && Array.isArray(el?.contents) && el.contents.length > 0) {
      el.contents.forEach(content => {
        if (content.style?.isVariable) {
          let variableDetails = globalVariables.filter(
            globalVariable =>
              globalVariable.id ===
              parseInt(content.content.replaceAll('{', '').replaceAll('}', '')),
          );
          if (variableDetails?.length > 0) {
            variableDetails = variableDetails[0];
            variableContract.set(variableDetails.id, {
              type:
                variableDetails.type === 'ListString'
                  ? 'listOfStrings'
                  : variableDetails.type.toLowerCase(),
            });
          }
        }
      });
    }
    }
  });
  if (childBlocks.length > 0) {
    childBlocks.forEach(childBlock => {
      const isTopBlock = childBlock.uuid === tableProperties?.topBlock;
      if (
        !isTable ||
        (isTopBlock ? tableProperties.showTopBlock : tableProperties.showBottomBlock)
      ) {
        childBlock.variableContract.forEach(childBlockVariable => {
          const { variableId, ...details } = childBlockVariable;
          if (variableId) {
            childBlockContract.set(variableId, details);
          } else if (details.type === VARIABLE_TYPE.FORM_OBJECT || details.type === VARIABLE_TYPE.CHART_OBJECT) {
            childBlockContract.set(details.name, details);
          } else if (details?.name?.includes(LINK_DISPLAY_TEXT)) {
            childBlockContract.set(details.name, { type: LINK_DISPLAY_TEXT });
          }
        });
      }
    });
  }
  if (isTable && tableProperties.emptyText) {
    if (tableProperties.emptyText?.length > 0) {
      tableProperties.emptyText.forEach(textContent => {
        if (textContent.style?.isVariable) {
          let variableDetails = globalVariables.filter(
            globalVariable =>
              globalVariable.id ===
              parseInt(textContent.content.replaceAll('{', '').replaceAll('}', '')),
          );
          if (variableDetails?.length > 0) {
            variableDetails = variableDetails[0];
            variableContract.set(variableDetails.id, {
              type:
                variableDetails.type === 'ListString'
                  ? 'listOfStrings'
                  : variableDetails.type.toLowerCase(),
            });
            updateLinkDisplayVariable(textContent, variableDetails, variableContract);
          }
        }
      });
    }
  }
  // TODO : Calculate field names
  let fieldNames = [];
  // TODO : Calculate table names, repetition blocks
  let tableNames = [];
  let variablesList = [];
  if (childBlockContract.size > 0) {
    if (!isTable || (isTable && !blockDetails?.variableContract[0].isArray)) {
      childBlockContract.forEach((value, key) => {
        variableContract.set(key, value);
      });
    }
  }
  variablesList = convertMapToList(variableContract);
  if (isRepetitive && !isTable) {
    variablesList = [{
      name: variableName,
      type: 'object',
      content: variablesList
    }]
  } else if (isTable) {
    const content = [];
    let isRepeatVariable = false;
    const isAlreadyRepetitive = blockDetails.variableContract[0]?.isArray;
    const repeatVariables = contents.reduce((acc, details) => {
      if (details.repeatVariable) {
        const split = details.repeatVariable.split('.').slice(0);
        console.log("Coming here :: ", details.repeatVariable, split);
        updateNestedObject(content, split, 'object', undefined, true);
        split.forEach(variable => {
          if (!acc.includes(variable)) {
            acc = [...acc, variable];
          }
        });
      }
      return acc;
    }, []);
    contents.forEach(el => {
      return el.content.contents.forEach(textContent => {
        if (
          textContent.style?.isVariable &&
          textContent.style?.parentEntity &&
          textContent.style?.entityName
        ) {
          isRepeatVariable = true;
          const split = textContent.style?.parentEntity.split('.').concat(textContent.style?.entityName).slice(0);
          updateNestedObject(content, split, 'object', undefined, false);
        }
      });
    });
    contents.forEach(el => {
      if (el.content.contents.length > 0) {
        el.content.contents.forEach(textContent => {
          if(textContent.style?.isVariable) {
            let variableDetails = globalVariables.filter(globalVariable => globalVariable.id === parseInt(textContent.content.replaceAll("{","").replaceAll("}","")));
            if (variableDetails?.length > 0) {
              variableDetails = variableDetails[0];
              if (isRepeatVariable) {
                addVariableToContent(content, variableDetails);
              } else {
                if (content.find(cellVar => cellVar.name === variableDetails.id) === undefined) {
                  content.push({
                    variableId: variableDetails.id,
                    type:
                      variableDetails.type === 'ListString'
                        ? 'listOfStrings'
                        : variableDetails.type.toLowerCase(),
                  });
                  // updateLinkDisplayVariable(content, variableDetails, variableContract);
                }
              }
            } else {
              const splitLocalVariable = textContent.content.split('.');
              updateNestedObject(content, splitLocalVariable, textContent.style?.variableType, textContent.style?.dateFormat);
            }
          }
        })
      }
      if (Array.isArray(el?.content?.contents) && el.content.contents.length > 0) {
        el.content.contents.forEach(textContent => {
          if (
            textContent.content === 'custom_logic' &&
            Array.isArray(textContent.contents) &&
            textContent.contents.length > 0
          ) {
            textContent.contents.forEach(cont => {
              if (cont?.style?.isVariable) {
                let variableDetails = globalVariables.filter(
                  globalVariable =>
                    globalVariable.id ===
                    parseInt(cont.content.replaceAll('{', '').replaceAll('}', '')),
                );
                if (variableDetails?.length > 0) {
                  variableDetails = variableDetails[0];
                  if (
                    content.find(cellVar => cellVar.name === variableDetails.id) === undefined
                  ) {
                    content.push({
                      variableId: variableDetails.id,
                      type:
                        variableDetails.type === 'ListString'
                          ? 'listOfStrings'
                          : variableDetails.type.toLowerCase(),
                    });
                  }
                }
              }
            });
          }
        });
      }
    });
    let tableVariable = isRepeatVariable && content.length > 0 ? content[0] : {
      name: variableName,
      type: 'object',
      content
    };
    if (isAlreadyRepetitive) {
      tableVariable.name = blockDetails?.variableContract[0]?.content.find(contract => contract.type === 'object')?.name;
      tableVariable = {
        name: blockDetails?.variableContract[0]?.name,
        type: 'object',
        isArray: true,
        content: [tableVariable, ...convertMapToList(childBlockContract)]
      }
    }
    console.log({ tableVariable });
    variablesList = [tableVariable, ...variablesList]; // Order of the variable to be the first one is important
  }
  return ({
    variables: variablesList,
    fieldNames,
    tableNames
  });
}

const addVariableToContent = (content, variableDetails) => {
  const variableObj = {
    variableId: variableDetails.id,
    type:
      variableDetails.type === 'ListString' ? 'listOfStrings' : variableDetails.type.toLowerCase(),
  };

  const targetName = variableDetails.variableCategoryName.toLowerCase();
  let added = false;

  function recurse(contents) {
    for (const item of contents) {
      if (item?.name && item.name.toLowerCase() === targetName) {
        item.content.push(variableObj);
        added = true;
        return true; // stop once added
      }
      if (item?.content && item.content.length > 0) {
        if (recurse(item.content)) return true;
      }
    }
    return false;
  }

  recurse(content);

  // if nothing matched, add it to the first content
  if (!added && content.length > 0) {
    content[0].content.push(variableObj);
  }
};

export const getGroupsFromMap = (contents = [], groupDetails = new Map() ) => {
  const groups = [];
  const temp = new Set();
  if (contents?.length > 0) {
    contents.forEach(content => {
      const groupId = content.groupId;
      if (!groupId || temp.has(groupId)) return;

      const groupDetail = groupDetails.get(groupId);
      if (groupDetail) {
        groups.push({ groupId, groupName: groupDetail.groupName });
        temp.add(groupId);
      }
    });
  }
  return groups;
}


const getExtraInfo = (extraVariable, block) => {
    let listCheckboxElements = [];
    let radiobuttonElements = [];
    let comboboxElement;
    const contents = Array.isArray(block?.contents) ? block.contents : [];
    contents &&
      contents.forEach(el => {
        const type = el?.fillableType;
        const groupName = el?.formOptions?.groupName;
        const fieldName = el?.formOptions?.fieldName;
        if (groupName === extraVariable?.name) {
          if (type === FILLABLE_CHECKBOX_TYPE) {
            listCheckboxElements.push(el);
          } else if (type === FILLABLE_RADIO_BUTTON_TYPE) {
            radiobuttonElements.push(el);
          }
        } else if (
          fieldName === extraVariable?.name &&
          type === FILLABLE_COMBO_BOX_TYPE
        ) {
          comboboxElement = el;
        }
      });
    return { listCheckboxElements, radiobuttonElements, comboboxElement };
};

export const getStoreStaticTableBlock = (block, store, isAttachment = false) => {
  let contents = block.contents;
  const type = block.type || block.blockType;
  if (Array.isArray(contents)) {
    let isUpdated = false;
    if (type === 'parent') {
      isUpdated = true;
      contents = block.contents.flatMap(el => {
        const element = store[el];
        if (element.type === STATIC_TABLE_BLOCK) {
          if (isAttachment) {
            return block.contents
              .map(cell => cell.contents)
              .flat()
              .filter(item => Object.keys(item).length > 0);
          }
          return getStoreStaticTableContents(element?.contents, store);
        }
        return element?.contents;
      });
    } else if (type === STATIC_TABLE_BLOCK) {
      isUpdated = true;
      if (isAttachment) {
        contents = block.contents
          .map(cell => cell.contents)
          .flat()
          .filter(item => Object.keys(item).length > 0);
      } else {
        contents = getStoreStaticTableContents(block?.contents, store);
      }
    }
    if (isUpdated) {
      const tempBlock = deepCopy(block);
      tempBlock.contents = contents;
      return tempBlock;
    }
  }
  return block;
};

export const getLocalVariable = (blockVariable, block = undefined, allVariables = [], isRoot = true, parent = undefined) => {
    const variableDetails = allVariables.find(gVar => gVar.id === blockVariable.variableId);
    if (variableDetails) return variableDetails;

    const localVariable = {
      name: `${parent ? `${parent}.` : ''}${blockVariable.name}`,
      placeHolderText: blockVariable.name,
      type: blockVariable.type === 'object' && block?.blockType === DYNAMIC_TABLE_BLOCK
        ? VARIABLE_TYPE.TABLE_OBJECT
        : blockVariable.type,
    };

    if (blockVariable.type === VARIABLE_TYPE.CHART_OBJECT) {
      return {
        ...localVariable,
        placeHolderText: blockVariable.name,
        variables: blockVariable.content.map(item2 => {
          return allVariables.find(gVar => gVar.id === item2.variableId);
        }),
      };
    } else if (blockVariable.type === 'object' && block.blockType === DYNAMIC_TABLE_BLOCK) {
      localVariable.type = VARIABLE_TYPE.TABLE_OBJECT;
      localVariable.isArray = blockVariable.isArray;
      if (isRoot) {
        if (blockVariable.isArray) {
          localVariable.type = blockVariable.type;
        }
      }
    }

    const { listCheckboxElements, radiobuttonElements, comboboxElement } = getExtraInfo(
      blockVariable,
      block,
    );
    if (listCheckboxElements?.length) {
      localVariable.extraInfo = listCheckboxElements;
    } else if (radiobuttonElements?.length) {
      localVariable.listInfo = radiobuttonElements;
    } else if (comboboxElement) {
      localVariable.numberInfo = comboboxElement;
    }

    if (VARIABLE_OBJECT_TYPES.includes(blockVariable.type) || VARIABLE_TYPE.FORM_OBJECT === blockVariable.type) {
      localVariable.variables = (blockVariable.content || []).map(item => {
        return getLocalVariable(item, block, allVariables, false, block.blockType === DYNAMIC_TABLE_BLOCK ? localVariable.name : '');
      })
    }

    return localVariable;
}

export const mergeLocalVariable = (variableDetails, blockVariable, allVariables, block) => {
  if (VARIABLE_OBJECT_TYPES.includes(variableDetails.type) || VARIABLE_TYPE.TABLE_OBJECT === variableDetails.type) {
    let variablesToAdd = [];
    (blockVariable.content || []).forEach(item => {
      let index = variableDetails.variables.findIndex(e => item.variableId ? e.id === item.variableId : e.placeHolderText === item.name);
      if (index === -1) {
        const localVariable = getLocalVariable(item, block, allVariables, false, block.blockType === DYNAMIC_TABLE_BLOCK ? variableDetails.name : '');
        variablesToAdd.push(localVariable);
      } else {
        variableDetails.variables[index] = mergeLocalVariable(
          variableDetails.variables[index],
          item,
          allVariables,
          block
        )
      }
    });
    if (variablesToAdd.length > 0) {
      variableDetails.variables = [...variableDetails.variables, ...variablesToAdd];
    }
  }
  return variableDetails;
}

export const processCachedDataSet = (sampleData, datasetCache) => {
  const calculateDataSet = (localSampleData, cached, parent= undefined) => {
    let cachedSample = {};
    if (cached) {
      if (cached.type === VARIABLE_TYPE.CHART_OBJECT) {
        // check if header array is equal for charts, if equal, no change in variables selected.
        if (
          stringifyEquals(
            cached.value[0].value[0].value.header,
            localSampleData.value[0].value[0].value.header,
          )
        ) {
          cachedSample = cached;
        } else {
          cachedSample = localSampleData;
        }
      } else if (cached.type === VARIABLE_TYPE.OBJECT || cached.type === VARIABLE_TYPE.TABLE_OBJECT) {
        let cacheUpdate = false;
        for (let cacheIndex = 0; cacheIndex < cached.value.length; cacheIndex++) {
          const samArray = localSampleData.value[0].value;
          const cacheArray = cached.value[cacheIndex].value;
          const sampleArray = [];
          samArray.forEach(sItem => {
            const cachedItem = cacheArray.find(cItem => cItem.name === sItem.name);
            if (cachedItem) {
              if (cachedItem.type === VARIABLE_TYPE.CHART_OBJECT) {
                if (stringifyEquals(cachedItem.value.header, sItem.value.header)) {
                  sampleArray.push(cachedItem);
                } else {
                  sampleArray.push(sItem);
                }
              } else if (cachedItem.type === VARIABLE_TYPE.TABLE_OBJECT) {
                const calculatedDataSet = calculateDataSet(sItem.value, cachedItem.value, `${cachedItem?.parent ? `${cachedItem?.parent}.${cachedItem?.subKey}` : cachedItem?.subKey}`);
                sampleArray.push({...cachedItem, value: calculatedDataSet});
              } else {
                sampleArray.push(cachedItem);
              }
            } else {
              sampleArray.push({...sItem, parent});
            }
          });
          if (sampleArray.length) {
            cacheUpdate = true;
            cached.value[cacheIndex].value = sampleArray;
          }
        }
        if (cacheUpdate) {
          cachedSample = cached;
        } else {
          cachedSample = localSampleData;
        }
      } else {
        cachedSample = cached;
      }
    } else {
      cachedSample = localSampleData;
    }
    return cachedSample;
  }
  const mergedSample = {};
  Object.keys(sampleData).forEach(key => {
    mergedSample[key] = calculateDataSet(sampleData[key], datasetCache[key]);
  });
  console.log({ mergedSample });
  return mergedSample;
}

export const getRepetitionContract = (blockVariables, repeat, variableName, isDynamicTable = false, isTableName = false) => {
  if (blockVariables.length > 0) {
    const firstVariable = blockVariables[0];
    if (firstVariable.type !== 'object' || isDynamicTable) {
      if (repeat) {
        if (isDynamicTable) {
          if (firstVariable.isArray) { // first object isArray
            if (isTableName) {
              return [{
                ...firstVariable,
                content: firstVariable.content.map(ct => {
                  if (ct.type === 'object') {
                    return Object.assign({}, ct, { name: variableName });
                  }
                  return ct;
                })
              }];
            }
            return [{
              ...firstVariable,
              name: variableName,
            }];
          } else if (isTableName) {
            return [{
              ...firstVariable,
              name: variableName,
            }];
          }
        }
        return [{
          name: variableName,
          type: 'object',
          content: blockVariables,
          isArray: isDynamicTable
        }]
      } else {
        return firstVariable.content;
      }
    } else {
      if (repeat) {
        return [{
          ...firstVariable,
          name: variableName,
        }];
      } else {
        return firstVariable.content;
      }
    }
  } else {
    if (repeat) {
      return [{
        name: variableName,
        type: 'object',
        content: []
      }];
    }
  }
  return blockVariables;
}

export const getRangeOfStaticTable = (tableElement, selectedRange, swapByOrder = true) => {
    const startCell = tableElement.contents.find(cell => cell.uuid === selectedRange.start);
    let endCell = selectedRange.end ? tableElement.contents.find(cell => cell.uuid === selectedRange.end) : undefined;
    if (!endCell) endCell = startCell;
    // console.log({ startCell, endCell });

    let rowStartIndex = startCell.row - 1,
        rowEndIndex = endCell.row + endCell.rowSpan - 2,
        colStartIndex = startCell.column - 1,
        colEndIndex = endCell.column + endCell.columnSpan - 2;

    if (swapByOrder && rowStartIndex > rowEndIndex) {
      let swap = rowEndIndex;
      rowEndIndex = rowStartIndex;
      rowStartIndex = swap;
    }

    if (swapByOrder && colStartIndex > colEndIndex) {
      let swap = colEndIndex;
      colEndIndex = colStartIndex;
      colStartIndex = swap;
    }

    let minRowIndex = rowStartIndex, maxRowIndex = rowEndIndex, minColIndex = colStartIndex, maxColIndex = colEndIndex;

    console.log({ rowStartIndex, rowEndIndex, colStartIndex, colEndIndex });

    let mergedCells = tableElement.contents
      .filter(cell => cell.rowSpan !== 1 || cell.columnSpan !== 1)
      .map(cell => Object.assign({}, {
        uuid: cell.uuid,
        row: cell.row,
        rowSpan: cell.rowSpan,
        column: cell.column,
        columnSpan: cell.columnSpan,
        rows: (new Array(cell.rowSpan)).fill(0).map((e, index) => cell.row - 1 + index),
        columns: (new Array(cell.columnSpan)).fill(0).map((e, index) => cell.column - 1 + index),
      })).sort((a, b) => {
        if (a.row > b.row && a.column > b.column) {
          return 1;
        } else {
          if (a.column >= b.column) {
            if (a.row > b.row) {
              return 1;
            } else if (a.row < b.row) {
              return -1;
            }
          } else if (a.row >= b.row) {
            if (a.column > b.column) {
              return 1;
            } else if (a.column < b.column) {
              return -1;
            }
          }
          return 0;
        }
        return 0;
      });

    let visitedCells = [];

    let hasMergedCells = mergedCells;

    do {

    hasMergedCells = mergedCells.filter(mergedCell => {
      if (visitedCells.includes(mergedCell.uuid)) return false;
      const isWithinRowRange = (mergedCell.row - 1 >= minRowIndex && mergedCell.row - 1 <= maxRowIndex) ||
          (mergedCell.row + mergedCell.rowSpan - 2 >= minRowIndex && mergedCell.row + mergedCell.rowSpan - 2 <= maxRowIndex) ||
          mergedCell.rows.includes(minRowIndex) || mergedCell.rows.includes(maxRowIndex);
      const isWithinColRange = (mergedCell.column - 1 >= minColIndex && mergedCell.column - 1 <= maxColIndex) ||
          (mergedCell.column + mergedCell.columnSpan - 2 >= minColIndex && mergedCell.column + mergedCell.columnSpan - 2 <= maxColIndex) ||
          mergedCell.columns.includes(minColIndex) || mergedCell.columns.includes(maxColIndex);
        if (isWithinRowRange && isWithinColRange) {
          visitedCells.push(mergedCell.uuid);
          return true;
        }
    });

    if (hasMergedCells.length !== 0) {

    hasMergedCells.forEach(mergedCell => {
      if (mergedCell.row < minRowIndex + 1 &&
        (mergedCell.row + mergedCell.rowSpan - 1 >= minRowIndex + 1 && mergedCell.row + mergedCell.rowSpan - 1 <= maxRowIndex + 1)) {
        minRowIndex = Math.min(minRowIndex, mergedCell.row - 1);
      } else if ((mergedCell.row >= minRowIndex + 1 && mergedCell.row <= maxRowIndex + 1) && mergedCell.row + mergedCell.rowSpan - 1 > maxRowIndex + 1) {
        maxRowIndex = Math.max(maxRowIndex, mergedCell.row + mergedCell.rowSpan - 2);
      } else if (mergedCell.rows.includes(minRowIndex) || mergedCell.rows.includes(maxRowIndex)) {
        minRowIndex = Math.min(minRowIndex, mergedCell.row - 1);
        maxRowIndex = Math.max(maxRowIndex, mergedCell.row + mergedCell.rowSpan - 2);
      }

      if (mergedCell.column < minColIndex + 1 && (mergedCell.column + mergedCell.columnSpan - 1 >= minColIndex + 1 && mergedCell.column + mergedCell.columnSpan - 1 <= maxColIndex + 1)) {
        minColIndex = Math.min(minColIndex, mergedCell.column - 1);
      } else if ((mergedCell.column >= minColIndex + 1 && mergedCell.column <= maxColIndex + 1) && mergedCell.column + mergedCell.columnSpan - 1 > maxColIndex + 1) {
        maxColIndex = Math.max(maxColIndex, mergedCell.column + mergedCell.columnSpan - 2);
      } else if (mergedCell.columns.includes(minColIndex) || mergedCell.columns.includes(maxColIndex)) {
        minColIndex = Math.min(minColIndex, mergedCell.column - 1);
        maxColIndex = Math.max(maxColIndex, mergedCell.column + mergedCell.columnSpan - 2);
      }
    });

    const newCells = hasMergedCells.length > 0 ? mergedCells.filter(mergedCell => {
      return hasMergedCells.find(cell => cell.uuid === mergedCell.uuid) === undefined;
    }) : [];

    if (newCells.length > 0) {
      hasMergedCells = [...newCells, ...hasMergedCells];
    } else {
      hasMergedCells = [];
    }

    }

    } while (hasMergedCells.length !== 0)

    console.log({ minRowIndex, maxRowIndex, minColIndex, maxColIndex });

    return ({
      rowStartIndex: minRowIndex,
      rowEndIndex : maxRowIndex,
      colStartIndex: minColIndex,
      colEndIndex: maxColIndex
    });
}

export const groupRows = (staticTableContents) => {
  const rows = Object.groupBy(staticTableContents, (content) => content.row);
  let rowLength = 1;
  const actualRows = Object.keys(rows).reduce((acc, row) => {
    let rowIndex = parseInt(row);
    let rowsProcessed = 1;
    if (rowIndex === rowLength) {
      acc[row] = rows[row];
    } else {
      let currentIndex = rowLength;
      while (currentIndex < rowIndex) {
        acc[currentIndex] = {};
        currentIndex++;
        rowsProcessed += 1;
      }
      acc[row] = rows[row];
    }
    rowLength += rowsProcessed;
    return acc;
  }, {});
  return actualRows;
}

export const getRangeOfCellsUUID = (startUUID, endUUID, tableData) => {
  if (!startUUID) return [];

  const startCell = tableData.find(cell => cell.uuid === startUUID);
  if (!startCell) return [];

  if (!endUUID) return [startUUID];

  const endCell = tableData.find(cell => cell.uuid === endUUID);
  if (!endCell) return [startUUID];

  const minRow = Math.min(startCell.row, endCell.row);
  const maxRow = Math.max(startCell.row, endCell.row);
  const minColumn = Math.min(startCell.column, endCell.column);
  const maxColumn = Math.max(startCell.column, endCell.column);

  const uuids = tableData
    .filter(
      cell =>
        cell.row >= minRow &&
        cell.row <= maxRow &&
        cell.column >= minColumn &&
        cell.column <= maxColumn,
    )
    .map(cell => cell.uuid);

  return uuids;
};

export const sortGridData = (data, order = 'asc') => {
  return data.sort((a, b) => {
    if (order === 'asc') {
      if (a.row === b.row) {
        return a.column - b.column;
      }
      return a.row - b.row;
    } else if (order === 'desc') {
      if (a.row === b.row) {
        return b.column - a.column;
      }
      return b.row - a.row;
    }
    return 0;
  });
};


export const updateStoreForNewFlow = (existingFlow, newFlow, persistedKeys, headerUUID, footerUUID, blocksUUID, blockMap, changesInStore, count, persistOnlyStandalone = false, blockIdsMap, currentDesign, isEmailTemplate = false, elementBusinessLogicData =[], elementBusinessLogicMap,isCloned = false) => {
    if (changesInStore[newFlow.uuid] === undefined) {
      changesInStore[newFlow.uuid] = newFlow;
    }
    if (persistOnlyStandalone ? (existingFlow.flowName === TABLE_OF_CONTENTS || existingFlow.isStandalone) : true) {
      persistedKeys[newFlow.uuid] = existingFlow.flowId;
    }
      // Syncing header and footer
      if (existingFlow.header && !existingFlow.header.isDeleted) {
        const isStandaloneHeader = existingFlow.header.isStandalone;
        let existingFlowHeader = isStandaloneHeader
          ? existingFlow.header.blockDTOList[0]
          : existingFlow.header;

          if (isStandaloneHeader) {
            const { updatedBlocks } = autoTransform(
              [existingFlowHeader] || [],
              existingFlow.style,
              undefined,
              undefined,
            );
            existingFlowHeader = updatedBlocks[0];
          }

          if (
            existingFlow.header.blockType === CREATIVE_BLOCK &&
            existingFlow.header?.groups?.length > 0 &&
            elementBusinessLogicMap
          ) {
            existingFlow.header.groups.forEach(group => {
              const isBLPresent = elementBusinessLogicData.some(el =>
                el.rules[0]?.groupIDs?.includes(group.groupId),
              );

              elementBusinessLogicMap.set(group.groupId, {
                groupName: group.groupName,
                isBLPresent,
              });
            });
          }

        const headerBlock = Object.assign({}, existingFlowHeader, {
          ...(isStandaloneHeader && {blockId: existingFlow.header.blockId}),
          uuid: uuidv4(),
          type: HEADER,
          style: {...existingFlowHeader.style, ...existingFlow.header.style}
        });
        if (blockIdsMap) {
          blockIdsMap.set(existingFlowHeader.uuid, headerBlock.uuid);
        }
        updateStoreForBlock(
          existingFlowHeader,
          headerBlock,
          persistedKeys,
          [],
          {},
          changesInStore,
          count,
          undefined,
          persistOnlyStandalone,
          isEmailTemplate,
          existingFlow?.flowName === TABLE_OF_CONTENTS,
          elementBusinessLogicMap,
          isCloned
        );
        isStandaloneHeader && delete headerBlock.blockDTOList;
        isStandaloneHeader && delete headerBlock.orderNumber;
        !isStandaloneHeader && delete headerBlock.blockId;

        changesInStore[newFlow.uuid].header = headerBlock.uuid;
        headerUUID = headerBlock.uuid;
        changesInStore[headerBlock.uuid] = headerBlock;
        if (existingFlow.header.blockId) {
          if (persistOnlyStandalone ? isStandaloneHeader : true) {
            persistedKeys[headerBlock.uuid] = existingFlow.header.blockId;
          }
        }
      }
      if (existingFlow.footer && !existingFlow.footer.isDeleted) {
        const isStandaloneFooter = existingFlow.footer.isStandalone;
        let existingFlowFooter = isStandaloneFooter
          ? existingFlow.footer.blockDTOList[0]
          : existingFlow.footer;
          if (isStandaloneFooter) {
            const { updatedBlocks } = autoTransform(
              [existingFlowFooter] || [],
              existingFlow.style,
              undefined,
              undefined,
            );
            existingFlowFooter = updatedBlocks[0];
          }

          if (
            existingFlow.footer.blockType === CREATIVE_BLOCK &&
            existingFlow.footer?.groups?.length > 0 &&
            elementBusinessLogicMap
          ) {
            existingFlow.footer.groups.forEach(group => {
              const isBLPresent = elementBusinessLogicData.some(el =>
                el.rules[0]?.groupIDs?.includes(group.groupId),
              );

              elementBusinessLogicMap.set(group.groupId, {
                groupName: group.groupName,
                isBLPresent,
              });
            });
          }

        const footerBlock = Object.assign({}, existingFlowFooter, {
          ...(isStandaloneFooter && {blockId: existingFlow.footer.blockId}),
          uuid: uuidv4(),
          type: FOOTER,
          style: {...existingFlowFooter.style, ...existingFlow.footer.style}
        });
        if (blockIdsMap) {
          blockIdsMap.set(existingFlowFooter.uuid, footerBlock.uuid);
        }
        updateStoreForBlock(
          existingFlowFooter,
          footerBlock,
          persistedKeys,
          [],
          {},
          changesInStore,
          count,
          undefined,
          persistOnlyStandalone,
          existingFlow?.flowName === TABLE_OF_CONTENTS.at,
          isEmailTemplate,
          elementBusinessLogicMap,
          isCloned
        );
        isStandaloneFooter && delete footerBlock.blockDTOList;
        isStandaloneFooter && delete footerBlock.orderNumber;
        !isStandaloneFooter && delete footerBlock.blockId;

        changesInStore[newFlow.uuid].footer = footerBlock.uuid;
        footerUUID = footerBlock.uuid;
        changesInStore[footerBlock.uuid] = footerBlock;
        if (existingFlow.footer.blockId) {
          if (persistOnlyStandalone ? isStandaloneFooter : true) {
            persistedKeys[footerBlock.uuid] = existingFlow.footer.blockId;
          }
        }
      }
      if (existingFlow?.style?.pageNumber && Object.keys(existingFlow.style.pageNumber).length > 0) {
        const pageNumber = {
          ...existingFlow.style.pageNumber,
          uuid: uuidv4(),
          contents: { ...existingFlow.style.pageNumber.contents, contentType: 'text' },
        };
        changesInStore[newFlow.uuid].pageNumber = pageNumber.uuid;
        changesInStore[pageNumber.uuid] = pageNumber;
      }
      if (existingFlow.blocks.length > 0) {
        if(existingFlow.flowName === TABLE_OF_CONTENTS && existingFlow.blocks.length < 1){
          existingFlow.blocks= staticTocFlow.blocks;
        }
        existingFlow.blocks.forEach(block => {
          if (block.isDeleted) {
            blocksUUID.push(block.uuid);
          } else {
            if (block.blockType === CREATIVE_BLOCK && block.groups?.length > 0 && elementBusinessLogicMap) {
              block.groups.forEach(group => {
                const isBLPresent = elementBusinessLogicData.some(el =>
                  el.rules[0]?.groupIDs?.includes(group.groupId),
                );

                elementBusinessLogicMap.set(group.groupId, {
                  groupName: group.groupName,
                  isBLPresent,
                });
              });
            }
            const isTocFlow = existingFlow.flowName === TABLE_OF_CONTENTS;
            const newBlock = Object.assign({}, block, {
              uuid: (isTocFlow && block.uuid)? block.uuid : uuidv4(),
              type: block.blockType,
              ...(currentDesign === 'document' && existingFlow.isStandalone &&
                !block.isStandalone && {
                  style: {
                    ...block.style,
                    fontFamily: existingFlow.style.fontFamily,
                    fontSize: existingFlow.style.fontSize,
                  },
                }),
            });
            if (blockIdsMap) {
              blockIdsMap.set(block.uuid, newBlock.uuid);
            }
            if (newBlock.blockType !== STANDALONE_BLOCK) {
              delete newBlock.blockId;
            }
            delete newBlock.orderNumber;
            updateStoreForBlock(
              block,
              newBlock,
              persistedKeys,
              blocksUUID,
              blockMap,
              changesInStore,
              count,
              newFlow,
              persistOnlyStandalone,
              isEmailTemplate,
              existingFlow?.flowName === TABLE_OF_CONTENTS,
              elementBusinessLogicMap,
              isCloned
            );
          }
        });
      }
}

export const updateStoreForBlock = (block, newBlock, persistedKeys, blocksUUID, blockMap, changesInStore, count, newFlow = undefined, persistOnlyStandalone = false, isEmailTemplate = false, isToc= false,groupMap = new Map(),isClone = false) => {
          if (newBlock.blockType === CREATIVE_BLOCK || newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
            const groupIdsArray =[];
            newBlock.contents = newBlock.contents.map(el => {
              if (el.contentType === ELEMENT_TYPES.CHART) {
                count.chartElementsCount += 1;
              } else if (el.contentType === STATIC_TABLE_ELEMENT) {
                return Object.assign({}, el, {
                  uuid: uuidv4(),
                  contents: el.contents.map(cell => Object.assign({}, cell, { uuid: uuidv4() }))
                });
              }
              //Creating new groupId while cloning the reusable pages/sections
              if (isClone) {
                const existingGroupId = el?.groupId;
                const newGroupId = uuidv4();
                if (existingGroupId && !groupIdsArray.some(item => item.existingGroupId === existingGroupId)) {
                  groupIdsArray.push({ existingGroupId: existingGroupId, newGroupId: newGroupId })
                }
                const newGroupIdfromArray = groupIdsArray.find(i => i.existingGroupId === existingGroupId)?.newGroupId ?? null;
                if (existingGroupId) {
                  const group_id = newGroupIdfromArray ? newGroupIdfromArray : newGroupId;
                  groupMap.set(group_id, { groupName: '', isBLPresent: false });
                  el.groupId = group_id;
                }
              }
              return Object.assign({}, el, { uuid: uuidv4() });
            });
            if (isClone) {
              const groupArray = getGroupsFromMap(newBlock.contents, groupMap);
              newBlock.groups = groupArray;
            }
            count.createBlocksCount += 1;
          }
          if (newBlock.blockType === STATIC_TABLE_BLOCK) {
            newBlock.contents = newBlock.contents.map(cell => {
              const uuid = uuidv4();
              cell.uuid = uuid;
              if (cell.contents.length > 0) {
                changesInStore[uuid] = Object.assign({}, cell.contents[0], {
                  isChild: true,
                  parentBlockUUID: newBlock.uuid,
                  uuid
                });
              }
              return Object.assign({}, cell, { contents: [] });
            });
          }
          if (newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
            (newBlock.blockDTOList || []).forEach(childBlock => {
              if (!childBlock.uuid) {
                childBlock.uuid = uuidv4();
              }
              const newChildBlock = Object.assign({}, childBlock, { type: childBlock.blockType });
              updateStoreForBlock(
                childBlock,
                newChildBlock,
                persistedKeys,
                [],
                blockMap,
                changesInStore,
                count,
                undefined,
                persistOnlyStandalone
              );
              if (childBlock.blockId) {
                if (childBlock.blockId !== 'topBlock' || childBlock.blockId !== 'bottomBlock') {
                  if (persistOnlyStandalone ? childBlock.isStandalone : true) {
                    persistedKeys[newChildBlock.uuid] = childBlock.blockId;
                  }
                }
              }
              newChildBlock.isChild = true;
              newChildBlock.parentBlockUUID = newBlock.uuid;
              if (childBlock.blockId) {
                blockMap.set(childBlock.blockId, newChildBlock.uuid);
              } else  {
                newChildBlock.uuid = uuidv4();
                blockMap.set(childBlock.uuid, newChildBlock.uuid);
              }
              delete newChildBlock.blockId;
              delete newChildBlock.orderNumber;
              changesInStore[newChildBlock.uuid] = newChildBlock;
            });
            newBlock.style = Object.assign({}, newBlock.style, {
              tableProperties: Object.assign({}, newBlock.style.tableProperties, {
                topBlock: newBlock.style.tableProperties.topBlock ? blockMap.get(newBlock.style.tableProperties.topBlock) : undefined,
                bottomBlock: newBlock.style.tableProperties.bottomBlock ? blockMap.get(newBlock.style.tableProperties.bottomBlock) : undefined,
              })
            })
            delete newBlock.blockDTOList;
          }
          if (newBlock.variableContract?.length > 0) { // TODO : Check whether this is executed only for newly dropped blocks, not already designed blocks
            if (newBlock.variableContract[0]?.name === undefined && newBlock.variableContract[0]?.type === 'object') {
              if(newBlock.variableContract[0]?.type === 'object')
              {
                let variableName = 'repeat'
                  if (newBlock.blockType === DYNAMIC_TABLE_BLOCK) {
                    variableName = 'table';
                  }
                  newBlock.variableContract[0] = Object.assign({}, newBlock.variableContract[0], {
                    name: `${variableName}-${count.createBlocksCount}`
                  })
                  count.createBlocksCount += 1;
              }
            }
          }
          if (newBlock.blockType === STANDALONE_BLOCK) {
            // saving the original standalone block details before autotransform for future transformation when changing flow Width
            newBlock.origContents = (deepCopy(newBlock.blockDTOList) || []).map((childBlock) => {
              const newChildBlockUUID = uuidv4();
              const new_childBlock = Object.assign({}, childBlock, {
                uuid: newChildBlockUUID,
                type: childBlock.blockType,
              });

              updateStoreForBlock(
                childBlock,
                new_childBlock,
                persistedKeys,
                [],
                blockMap,
                changesInStore,
                count,
                undefined,
                undefined,
                isEmailTemplate
              );
              changesInStore[new_childBlock.uuid] = new_childBlock;
              return newChildBlockUUID;
            });
            const { updatedBlocks } = autoTransform(
              newBlock.blockDTOList || [],
              isEmailTemplate ? { width: newFlow?.width, height: 1 } : newFlow,
              isEmailTemplate ? 590 : undefined,
              isEmailTemplate ? 1 : undefined,
            );
            newBlock.contents = (updatedBlocks || []).map((childBlock, index) => {
              const newChildBlockUUID = uuidv4();
              const new_childBlock = Object.assign({}, childBlock, {
                uuid: newChildBlockUUID,
                type: childBlock.blockType,
              });

              updateStoreForBlock(
                childBlock,
                new_childBlock,
                persistedKeys,
                [],
                blockMap,
                changesInStore,
                count,
                undefined,
                undefined,
                isEmailTemplate
              );
              // blocksUUID.push(new_childBlock.uuid); // Child should not be added as direct child to flow
              changesInStore[new_childBlock.uuid] = new_childBlock;
              return newChildBlockUUID;
            });
            delete newBlock.blockDTOList;
          }
          if (newFlow) {
            newFlow[`${newFlow.uuid}-0`].push(newBlock.uuid);
          }
          blocksUUID.push(newBlock.uuid);
          changesInStore[newBlock.uuid] = newBlock;
          if (block.blockId) {
            if (persistOnlyStandalone ? (isToc || block.isStandalone) : true) {
              persistedKeys[newBlock.uuid] = block.blockId;
            }
          }
}

export const autoTransformStandaloneBlocks = (details, existingFlow, store) => {
  const pageId = `${details.uuid}-0`;
  const blockUUIDs = existingFlow[pageId];
  const currentStore = store.get();
  const isEmailTemplate = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE];
  const changesInStore = {};
  const blocks = [];
  blockUUIDs.forEach(blockUUID => {
    const block = currentStore[blockUUID];
    blocks.push(block);
    if (block?.isStandalone) {
      const childBlocks =
        block?.origContents?.map(childBlockUUID => {
          return currentStore[childBlockUUID];
        }) || [];
      const { updatedBlocks } = autoTransform(
        deepCopy(childBlocks),
        isEmailTemplate
          ? { width: details.widthInPX, height: 1 }
          : { width: details.widthInPX, height: existingFlow.height },
        isEmailTemplate ? existingFlow.width : undefined,
        isEmailTemplate ? 1 : undefined,
        false,
        currentStore
      );
      block.contents.forEach((uuid, index) => {
        updatedBlocks[index].uuid = uuid;
        changesInStore[uuid] = updatedBlocks[index];
      });
    }
  });
  if (isEmailTemplate) {
    // transform blocks when email width changes.
    const { updatedBlocks } = autoTransform(
      deepCopy(blocks),
      isEmailTemplate
        ? { width: details.widthInPX, height: 1 }
        : { width: details.widthInPX, height: existingFlow.height },
      isEmailTemplate ? existingFlow.width : undefined,
      isEmailTemplate ? 1 : undefined,
      false,
      currentStore,
    );
    blockUUIDs.forEach((blockUUID, index) => {
      changesInStore[blockUUID] = updatedBlocks[index];
    });
  }
  return changesInStore;
};

export const getStoreStaticTableContents = (contents = [], store) => {
  return contents.map(cell => store[cell.uuid]).filter(cell => cell !== undefined);
};

export const DEFAULT_DOCUMENT_JSON = {
  language: 'en',
  style: {
    fontFamily: 'Open Sans',
    fontSize: 16
  },
  flows: []
};

export const DEFAULT_FLOW_JSON = (flowName, orderNumber, t) => {
  const pageNumber = getPgNoDetailsAtIndex(0, t);
  return {
    flowName,
    blocks: [],
    orderNumber,
    paperOrientation: 'portrait',
    paperSize: 'custom',
    style: {
      background: '#ffffff',
      pageNumber
    },
    flowId: uuidv4()
  };
};

export const DEFAULT_PAGE_NUMBER_JSON = (pageNumberType) => {
  return ({
      contents: {
        style: {},
        contents: [],
        contentType: "text"
      },
      pageNumberType
  });
}

export const DEFAULT_LINE_BREAK_JSON = () => {
  return ({
     contentType: "LINE_BREAK",
     style: {
       lineSpacing: 1.5,
       fontWeight: [],
       color: "#000000",
       alignment: "left"
     },
     content: "\n"
  });
}

export const DEFAULT_DYNAMIC_COLUMN_JSON = () => {
  return ({
      groupHeaderContent: DEFAULT_CELL_JSON(2, { columnSpan: 1 }),
      headerContent: DEFAULT_CELL_JSON(2),
      content: DEFAULT_CELL_JSON(2),
      uuid: uuidv4()
  });
}

export const DEFAULT_CELL_JSON = (defaultPadding = 0, extraAttr = {}) => {
  return ({
      style: {
          border: {
              thickness: 1,
              borderStyle: "SOLID",
              borderColor: "#000000",
              left: true,
              top: true,
              bottom: true,
              right: true
          },
          padding: {
              top: defaultPadding,
              right: defaultPadding,
              left: defaultPadding,
              bottom: defaultPadding
          },
          backgroundColor: "#ffffff00",
          verticalAlignment: 'center',
          horizontalAlignment: 'center'
      },
      /*contents: [
          {
              contentType: "PARAGRAPH",
              style: {
                  lineSpacing: 1.5,
                  fontWeight: [],
                  color: "#000000"
              },
              content: ""
          },
          {
              contentType: "LINE_BREAK",
              content: "\n",
              style: {
                  lineSpacing: 1.5,
                  alignment: "center",
                  color: "#000000"
              }
          }
      ],*/
      contents: [],
      ...extraAttr,
      uuid: uuidv4()
  });
}

export const constructDesignFromInstructions = (instructions, zipFileContents, isReusable = false, languageCode = 'en', referencedMap = {}, imageMap = {}, t) => {
  const designJSON = JSON.parse(JSON.stringify(DEFAULT_DOCUMENT_JSON));
  let currentFlowIndex = 0;
  let currentBlockIndex = 0;
  let currentElementIndex = undefined;
  let currentBlockEditing = undefined;
  let currentTableElementIndex = undefined;
  instructions.forEach(command => {
    // console.log({ tab: command.tabs, command: command.instruction, currentBlockIndex, currentBlockEditing, currentElementIndex, currentTableElementIndex });
    switch (command.tabs) {
      case 0:
        if (command.instruction.startsWith('Set document') || command.instruction.startsWith('Set Document')) {
          if (command.instruction.includes('language')) { // Set document language : en
            const language = command.instruction.split(":")[1].trim();
            designJSON.language = language;
          } else if (command.instruction.includes('style')) { // Set Document style - FontFamily : Open sans, FontSize: 16px
            const fontFamily = command.instruction.split(":")[1].split(",")[0].trim();
            const fontSize = command.instruction.split(":")[2].trim();
            designJSON.style = {
              fontFamily: fontFamily,
              fontSize: parseInt(fontSize.replaceAll('px', ''))
            };
          } else if (command.instruction.includes('Envelope')) { // Set Envelope : C5
            const envelopeType = command.instruction.split(":")[1].trim();
            designJSON.envelopeType = envelopeType;
          } else if (command.instruction.includes('Delivery Class')) { // Set Delivery Class : 1
            const deliveryClass = parseInt(command.instruction.split(":")[1].trim());
            designJSON.deliveryClass = deliveryClass;
          }
        } else if (command.instruction.startsWith('Add')) {
          if (command.instruction.includes('flow') || (isReusable && command.instruction.includes('reusable section'))) { // Add a flow - flow1
            const flowName = command.instruction.split('-')[1].trim();
            designJSON.flows.push(JSON.parse(JSON.stringify(DEFAULT_FLOW_JSON(flowName, designJSON.flows.length + 1, t))));
            currentFlowIndex = designJSON.flows.length - 1;
          }
        }
        // TODO : Add business logic
        // TODO : Add inserts?
        break;
      case 1:
        currentElementIndex = undefined;
        currentTableElementIndex = undefined;
        if (command.instruction.startsWith('Make it')) {
          if (command.instruction.includes('Landscape') || command.instruction.includes('Portrait')) { // Make it Portrait
            designJSON.flows[currentFlowIndex].paperOrientation = command.instruction.split(" ")[2].toLowerCase();
          } else if (command.instruction.includes('Height')) { // Make it Custom size - Height : 1123px, Width : 794px
            const height = command.instruction.split(":")[1].split(",")[0].trim();
            const width = command.instruction.split(":")[2].trim();
            designJSON.flows[currentFlowIndex].paperSize = 'custom';
            // TODO : Check if width and height understanding changes with orientation, if so change order in instruction
            designJSON.flows[currentFlowIndex].style = Object.assign({}, designJSON.flows[currentFlowIndex].style || {}, {
              width: parseInt(width.replaceAll('px', '')),
              height: parseInt(height.replaceAll('px', ''))
            });
          }
        } else if (command.instruction.startsWith('Set')) { // Set background #ffffff
          if (command.instruction.includes('background')) {
            designJSON.flows[currentFlowIndex].style.background = command.instruction.split(" ")[2];
          }
        } else if (command.instruction.startsWith('Add')) {
          if (command.instruction.includes('spaces')) { // Add spaces - Top : 0mm, Bottom : 0mm, Right : 0mm, Left : 0mm
            let padding = command.instruction.split('-')[1].split(',').map(m => {
              return parseInt(m.split(':')[1].replaceAll('px', '').replaceAll('mm', ''));
            });
            designJSON.flows[currentFlowIndex].style.padding = {
              left: padding[3],
              right: padding[2],
              top: padding[0],
              bottom: padding[1]
            };
          } else if (command.instruction.includes('header')) { // Add a header
            designJSON.flows[currentFlowIndex].header = Object.assign({}, {
              blockType: 'creative',
              contents: [],
              variableContract: [],
              style: {},
              blockId: uuidv4()
            });
            currentBlockEditing = 'header';
            currentBlockIndex = undefined;
          } else if (command.instruction.includes('footer')) { // Add a footer
            designJSON.flows[currentFlowIndex].footer = Object.assign({}, {
              blockType: 'creative',
              contents: [],
              variableContract: [],
              style: {},
              blockId: uuidv4()
            });
            currentBlockEditing = 'footer';
            currentBlockIndex = undefined;
          } else if (command.instruction.includes('page number')) {
            designJSON.style.pageNumber = DEFAULT_PAGE_NUMBER_JSON(3);
            currentBlockEditing = 'pageNumber';
            currentBlockIndex = undefined;
          } else if (command.instruction.includes('text section')) { // Add a text section - block1
            const blockName = command.instruction.split('-')[1].trim();
            designJSON.flows[currentFlowIndex].blocks.push(Object.assign({}, {
              blockType: 'text',
              orderNumber: designJSON.flows[currentFlowIndex].blocks.length + 1,
              blockName,
              contents: [],
              variableContract: [],
              style: {},
              blockId: uuidv4()
            }));
            currentBlockEditing = 'block';
            currentBlockIndex = designJSON.flows[currentFlowIndex].blocks.length - 1;
          } else if (command.instruction.includes('creative section')) { // Add a creative section - block2
            const blockName = command.instruction.split('-')[1].trim();
            designJSON.flows[currentFlowIndex].blocks.push(Object.assign({}, {
              blockType: 'creative',
              orderNumber: designJSON.flows[currentFlowIndex].blocks.length + 1,
              blockName,
              contents: [],
              variableContract: [],
              style: {},
              blockId: uuidv4()
            }));
            currentBlockEditing = 'block';
            currentBlockIndex = designJSON.flows[currentFlowIndex].blocks.length - 1;
          } else if (command.instruction.includes('dynamic table section')) { // Add a dynamic table section - block1
            const blockName = command.instruction.split('-')[1].trim();
            designJSON.flows[currentFlowIndex].blocks.push(Object.assign({}, {
              blockType: 'dynamic_table',
              orderNumber: designJSON.flows[currentFlowIndex].blocks.length + 1,
              blockName,
              contents: [],
              variableContract: [],
              style: {
                ...(DEFAULT_CELL_JSON(5).style),
                tableProperties: {
                  emptyText: [DEFAULT_LINE_BREAK_JSON()],
                  showBottomBlock: false,
                  showTopBlock: false,
                  showHeader: true,
                  showHeaderOnAllPages: true,
                  showtopBlockOnAllPages: false
                }
              },
              blockId: uuidv4()
            }));
            currentBlockEditing = 'block';
            currentBlockIndex = designJSON.flows[currentFlowIndex].blocks.length - 1;
          } else if (command.instruction.includes('static table section')) { // Add a static table section - block2
            const blockName = command.instruction.split('-')[1].trim();
            designJSON.flows[currentFlowIndex].blocks.push(Object.assign({}, {
              blockType: 'static_table',
              orderNumber: designJSON.flows[currentFlowIndex].blocks.length + 1,
              blockName,
              contents: [],
              variableContract: [],
              style: {
                tableProperties: {}
              },
              blockId: uuidv4()
            }));
            currentBlockEditing = 'block';
            currentBlockIndex = designJSON.flows[currentFlowIndex].blocks.length - 1;
          } else if (command.instruction.includes('reusable section')) { // Add a reusable section - block2
            console.log("Reusable section Instruction :: ", command.instruction);
            const blockName = (IMPORT_REUSABLE_PREFIX + ' ' + command.instruction.split('-')[1].trim()).toUpperCase(); // Add prefix to name (DKV IMPORT)
            let key = `${blockName}_${languageCode.toUpperCase()}`;
            if (languageCode !== 'en' && referencedMap[key] === undefined) {
              key = `${blockName}_EN`;
            }
            console.log("key :: ", key, blockName, referencedMap[key]);
            if (referencedMap[key]) {
              designJSON.flows[currentFlowIndex].blocks.push(Object.assign({}, {
                blockType: 'parent',
                blockId: referencedMap[key].blockId,
                isStandalone: true,
                blockDTOList: [...(referencedMap[key].blockDTOList || [])],
                orderNumber: designJSON.flows[currentFlowIndex].blocks.length + 1,
                blockName,
                variableContract: [...(referencedMap[key].variableContract || [])],
                style: Object.assign({}, referencedMap[key].style)
              }));
              currentBlockEditing = 'block';
              currentBlockIndex = designJSON.flows[currentFlowIndex].blocks.length - 1;
            }
          }
        }
        break;
      case 4:
        if (currentTableElementIndex == undefined) return;
        let isTableElement = true;
      case 3:
        if (currentElementIndex === undefined) return;
        let isElement = true;
      case 2:
        let editingBlock = currentBlockIndex === undefined
          ? currentBlockEditing === 'pageNumber'
            ? designJSON.style.pageNumber.contents
            : designJSON.flows[currentFlowIndex][currentBlockEditing]
          : designJSON.flows[currentFlowIndex].blocks[currentBlockIndex];
        if (isElement) {
          editingBlock = editingBlock.contents[currentElementIndex];
        }
        if (!isTableElement && currentTableElementIndex !== undefined) {
          currentTableElementIndex = undefined;
        }
        if (command.instruction.startsWith('Add')) {
          if (command.instruction.includes('spaces')) { // Add spaces - Top : 0mm, bottom: 0mm, Right: 10mm, Left: 10mm
            let padding = command.instruction.split('-')[1].split(',').map(m => {
              return parseInt(m.split(':')[1].replaceAll('px', '').replaceAll('mm', ''));
            });
            editingBlock.style.padding = {
              left: padding[3],
              right: padding[2],
              top: padding[0],
              bottom: padding[1]
            };
          } else if (command.instruction.includes('padding')) { // Add padding - Top : 5px, Bottom: 5px, Right: 5px, Left: 5px
            let padding = command.instruction.split('-')[1].split(',').map(m => {
              return parseInt(m.split(':')[1].replaceAll('px', '').replaceAll('mm', ''));
            });
            // console.log({ editingBlock });
            if (isTableElement) {
              editingBlock.contents[currentTableElementIndex].style.padding = {
                left: padding[3],
                right: padding[2],
                top: padding[0],
                bottom: padding[1]
              };
            } else {
              editingBlock.style.padding = {
                left: padding[3],
                right: padding[2],
                top: padding[0],
                bottom: padding[1]
              };
            }
          } else if (command.instruction.includes('border')) { // Add border - Top : 1px, Bottom: 1px, Right: 1px, Left: 1px
            /*let border = command.instruction.split('-')[1].split(',').map(m => {
              return parseInt(m.split(':')[1].replaceAll('px', '').replaceAll('mm', ''));
            });*/
          } else if (command.instruction.startsWith('Add text styles')) { // Add text styles - FontFamily : Arial, FontSize : 13px, Color : #000000, BackgroundColor : #ffffff
            if (!editingBlock.contents) return;
            let textElement = editingBlock.contents[editingBlock.contents.length - 1];
            if (isTableElement) {
              textElement = editingBlock.contents[currentTableElementIndex].contents;
              if (!textElement) return;
              textElement = textElement[textElement.length - 1];
            }
            // console.log({ textElement });
            if (textElement) {
              // textElement.style
              const styles = command.instruction.split('-')[1].split(',');
              styles.forEach(style => {
                if (style?.length) {
                  let key = style.split(':')[0].trim();
                  key = key.replace(/^.{1}/g, key[0].toLowerCase());

                  let value = style.split(':')[1].trim();

                  if (key.includes('fontSize')) { value = parseInt(value.replaceAll('px', '')); }
                  if (key.includes('lineSpacing')) { value = parseFloat(value); }

                  textElement.style[key] = value;
                }
              })
            }
          } else if (command.instruction.startsWith('Add text')) { // Add text : Dear (or) Add text : $Patient.new
            let text = command.instruction.replace('Add text : ', '');
            if (!text || !editingBlock.contents) return;
            /*if (text.startsWith('$')) {
              // TODO : Add variable and include id
            } else {*/
              if (isTableElement) {
                // console.log("editingBlock.contents[currentTableElementIndex].contents :: ", editingBlock)
                editingBlock.contents[currentTableElementIndex].contents.push({
                  contentType: "PARAGRAPH",
                  style: {
                     lineSpacing: 1.5,
                     fontWeight: [],
                     color: "#000000"
                  },
                  content: text
               });
              } else {
                editingBlock.contents.push({
                  contentType: "PARAGRAPH",
                  style: {
                     lineSpacing: 1.5,
                     fontWeight: [],
                     color: "#000000"
                  },
                  content: text
               });
             }
            // }
          } else if (command.instruction.includes('reusable')) {
            // TODO : Check if static table block support for images also needed
            console.log("reusable Instruction to image :: ", command.instruction);
            let imageName = command.instruction.split('-')[1].trim(); // Add prefix to name (DKV IMPORT)
            if (imageName.startsWith('Footer')) {
              console.log("footer identified :: ", command.instruction, referencedMap);
              let key = (IMPORT_REUSABLE_PREFIX + ' ' + imageName + '_en').toUpperCase();
              if (referencedMap[key]) {
                editingBlock = Object.assign({}, {
                   blockType: 'parent',
                   blockId: referencedMap[key].blockId,
                   isStandalone: true,
                   blockDTOList: [...(referencedMap[key].blockDTOList || [])],
                   blockName: imageName,
                   variableContract: [...(referencedMap[key].variableContract || [])],
                   style: Object.assign({}, referencedMap[key].style)
                 });
                 currentElementIndex = undefined;
              }
            } else {
              imageName = (IMPORT_REUSABLE_PREFIX + ' ' + imageName).toUpperCase();
              console.log({ imageName, imageMap });
              if (imageMap[imageName]) {
                editingBlock.contents.push({
                  contentType: 'image',
                  contents: [],
                  style: {},
                  url: imageMap[imageName],
                  uuid: uuidv4()
                })
                currentElementIndex = editingBlock.contents.length - 1;
              }
            }
          } else if (command.instruction.includes('text') || command.instruction.includes('address')) { // Add a text element || Add a text element - row : 1, column : 1, rowSpan: 1, columnSpan: 1
            let extraProperties = {};
            let extraStyleProperties = {};
            if (command.instruction.includes('row')) {
              extraProperties = command.instruction.split('-')[1].split(',').reduce((acc, val) => {
                return { ...acc, [val.split(':')[0].trim()]: parseInt(val.split(':')[1].trim()) };
              }, {});
              if (!extraProperties.columnSpan) {
                extraProperties.columnSpan = 1;
              }
              if (!extraProperties.rowSpan) {
                extraProperties.rowSpan = 1;
              }
              extraStyleProperties = DEFAULT_CELL_JSON(0).style;
            }
            const isAddressElement = command.instruction.includes('address');
            if (isAddressElement && designJSON.envelopeType && SUPPORTED_ENVELOPE_TYPES[designJSON.envelopeType]) {
              const style = SUPPORTED_ENVELOPE_TYPES[designJSON.envelopeType];
              // Assuming that the this editingBlock is the first block
              extraStyleProperties = {
                x: measurementConversion(style.x, 'px'),
                y: measurementConversion(style.y, 'px'),
                width: measurementConversion(style.width, 'px'),
                height: measurementConversion(style.height, 'px'),
                rotate: 0
              }
            }
            editingBlock.contents.push({
              contentType: 'text',
              contents: [],
              style: extraStyleProperties,
              uuid: uuidv4(),
              uiType: isAddressElement ? 'address' : undefined,
              ...extraProperties
            });
            if (isAddressElement) {
              designJSON.flows[currentFlowIndex].style.address = {
                blockUUID: editingBlock.blockId
              }
            }
            if (isElement && command.instruction.includes('row')) {
              isTableElement = true;
              currentTableElementIndex = editingBlock.contents.length - 1;
            } else {
              isTableElement = false;
              currentElementIndex = editingBlock.contents.length - 1;
            }
          } else if (command.instruction.includes('static table')) {
            editingBlock.contents.push({
              contentType: 'static_table',
              contents: [],
              style: {
                tableProperties: {},
                border: {
                  thickness: 1,
                  borderColor: "#000000",
                  borderStyle: "SOLID",
                  top: true,
                  right: true,
                  left: true,
                  bottom: true
                }
              },
              uuid: uuidv4()
            });
            currentElementIndex = editingBlock.contents.length - 1;
          } else if (command.instruction.includes('column')) {
            editingBlock.contents.push(DEFAULT_DYNAMIC_COLUMN_JSON());
            currentElementIndex = editingBlock.contents.length - 1;
          }
          // TODO : Support other basic elements - SVGs, barcodes, fillables, etc
        } else if (command.instruction.startsWith('Upload')) { // Upload image - email header
          // TODO : Check if static table block support for images also needed
          console.log("Instruction :: ", command.instruction);
          let imageName;
          if (command.instruction.includes('-')) {
            imageName = command.instruction.split('-')[1].trim().toUpperCase();
          } else {
            imageName = (IMPORT_REUSABLE_PREFIX + ' ' + command.instruction.split(':')[1].trim()).toUpperCase(); // Add prefix to name (DKV IMPORT)
          }
          console.log({ imageName, imageMap });
          if (imageMap[imageName]) {
            editingBlock.contents.push({
              contentType: 'image',
              contents: [],
              style: {},
              url: imageMap[imageName],
              uuid: uuidv4()
            })
            currentElementIndex = editingBlock.contents.length - 1;
          }
        } else if (command.instruction === 'Enter') { // Enter
          if (!editingBlock.contents) return;
          if (isTableElement) {
            editingBlock.contents[currentTableElementIndex].contents.push(DEFAULT_LINE_BREAK_JSON());
          } else {
            editingBlock.contents.push(DEFAULT_LINE_BREAK_JSON());
         }
        } else if (command.instruction.startsWith('Make it')) {
          if (command.instruction.includes('height')) { // Make it 230px in height
            if (editingBlock.uiType === 'address') return;
            const height = command.instruction.replaceAll('Make it ', '').split(' ')[0].replaceAll('px', '');
            editingBlock.style.height = parseInt(height);
          } else if (command.instruction.includes('width')) {
            if (editingBlock.uiType === 'address') return;
            const width = command.instruction.replaceAll('Make it ', '').split(' ')[0].replaceAll('px', '');
            editingBlock.style.width = parseInt(width);
          } else if (command.instruction.includes('Bold') || command.instruction.includes('Italic') || command.instruction.includes('Underlined')) {
              let textElement = editingBlock.contents;
              let style = command.instruction.replaceAll('Make it ', '').toLowerCase();
              if (command.instruction.includes('Italic')) {
                style = 'italics';
              } else if (command.instruction.includes('Underlined')) {
                style = 'underline';
              }
              if (isTableElement) {
                textElement = editingBlock.contents[currentTableElementIndex].contents;
                if (!textElement || textElement?.length === 0) return;
                textElement = textElement[textElement.length - 1];
                textElement.style.fontWeight = [...(textElement.style.fontWeight || []), style];
              } else {
                if (textElement === undefined || textElement?.length === 0) return;
                textElement = textElement[textElement.length - 1];
                textElement.style.fontWeight = [...(textElement.style.fontWeight || []), style];
             }
          }
        } else if (command.instruction.startsWith('Position')) { // Position it at (22, 0)
          if (editingBlock.uiType !== 'address') {
            const position = command.instruction.split('(')[1].split(',');
            editingBlock.style = Object.assign(editingBlock.style, {
              x: parseInt(position[0].replaceAll(')', '')),
              y: parseInt(position[1].replaceAll(')', '')),
              rotate: 0 // TODO : Integration with rotate
            });
          }
        } else if (command.instruction.startsWith('Create a grid')) { // Create a grid - Columns : 3, Rows: 2
          const columnCount = parseInt(command.instruction.split('-')[1].split(',')[0].split(':')[1].trim());
          const rowCount = parseInt(command.instruction.split('-')[1].split(',')[1].split(':')[1].trim());
          editingBlock.style.tableProperties.columns = columnCount;
          editingBlock.style.tableProperties.rows = rowCount;
          currentTableElementIndex = undefined;
        } else if (command.instruction.startsWith('Set column width')) { // Set column width : 30px, 30px, 30px (or even in %)
          const columnWidths = command.instruction.split(':')[1].split(',');
          editingBlock.style.tableProperties.columnWidths = columnWidths.map(width => parseInt(width.replaceAll('px', '').replaceAll('%', '')));
        } else if (command.instruction.startsWith('Set row heights')) { // Set row heights : 40px, 40px
          const rowHeights = command.instruction.split(':')[1].split(',');
          editingBlock.style.tableProperties.rowHeights = rowHeights.map(height => parseInt(height.replaceAll('px', '')));
        } else if (command.instruction.startsWith('Make')) {
          let borderColor = command.instruction.split(' ');
          borderColor = borderColor[borderColor.length - 1];

          if (isTableElement) {
            editingBlock.contents[currentTableElementIndex].style.border.borderColor = borderColor;
          } else {
            if (!editingBlock.style?.border) return;
            editingBlock.style.border.borderColor = borderColor;
          }
        } else if (command.instruction.startsWith('Set')) { // Set background #ffffff
          if (command.instruction.includes('background')) {
            let backgroundColor = command.instruction.split(' ');
            backgroundColor = backgroundColor[backgroundColor.length - 1];
            if (isTableElement) {
              editingBlock.contents[currentTableElementIndex].style.backgroundColor = backgroundColor;
            } else {
              editingBlock.style.backgroundColor = backgroundColor;
            }
          }
        }

        if (currentBlockIndex === undefined) {
          if (isElement) {
            designJSON.flows[currentFlowIndex][currentBlockEditing].contents[currentElementIndex] = editingBlock;
          } else {
            if (currentBlockEditing === 'pageNumber') {
              designJSON.style.pageNumber.contents = editingBlock;
            } else {
              designJSON.flows[currentFlowIndex][currentBlockEditing] = editingBlock;
            }
          }
        } else {
          if (isElement) {
            designJSON.flows[currentFlowIndex].blocks[currentBlockIndex].contents[currentElementIndex] = editingBlock;
          } else {
            designJSON.flows[currentFlowIndex].blocks[currentBlockIndex] = editingBlock;
          }
        }
        break;
        // TODO : Support properties for other elements
      default:
        console.log("Unknown instruction :: ", command);
        break;
    }
  });
  return designJSON;
}

export const getRepeatVariableDepth = (contents, relations) => {
  const relationMap = new Map(relations.map(r => [r.placeHolderText, r]));
  let deepestRelation = null;

  for (let i = 0; i < contents.length; i++) {
    const style = contents[i].style;
    if (!style?.parentEntity || !style?.entityName) continue;

    const parts = style.parentEntity.split(".");
    const depthLevel = parts.length;
    const combinedKey = parts.at(-1) + "." + style.entityName;
    const match = relationMap.get(combinedKey);

    if (match?.isList) {
      if (!deepestRelation || depthLevel > deepestRelation.depthLevel) {
        deepestRelation = {
          repeatVariable: `${style.parentEntity}.${style.entityName}`,
          parentEntity: style.parentEntity,
          entityName: style.entityName,
          depthLevel,
          match
        };
      }
    }
  }

  return deepestRelation;
}

export const constructTableContentsFromDataSet = (dataSet, key, contents, totalRows, currentRowIndex = 0, parentContext = undefined) => {
  const tableDataSet = dataSet[key];
  console.log({ tableDataSet, dataSet, key });
  let rows = [];
  // Populate tableContents, columnLevels and totalRows based on dataSet (contents of the table from the entire dataSet)
  tableDataSet.forEach((row, rowIndex) => {
      totalRows += 1;
      let newRow = [];
      let hasNest = false;
      contents.forEach((col, colIndex) => {
        if (hasNest) return;
        if (col.repeatVariable) {
          hasNest = true;
          const split = col.repeatVariable.split('.');
          const repeatKey = split[split.length - 1];
          /*if (key === repeatKey) {
            newRow.push({
              rowSpan: 1,
              rowIndex: currentRowIndex,
              dataSet: Object.assign({}, parentContext, {
                [repeatKey]: row
              }),
              ...col
            })
          } else {*/
          if (row[repeatKey]) {
            if (row[repeatKey].length > 0) {
            const modifiedContents = contents.slice(colIndex).map((c, i) => {
              if (i === 0 || c.repeatVariable === col.repeatVariable) {
                return Object.assign({}, c, { repeatVariable: undefined });
              }
              return c;
            });
            let upperContext = Object.keys(row).reduce((acc, k) => {
              if (k.includes('.')) {
                return { ...acc, [k]: row[k] };
              }
              return acc;
            }, {});
            upperContext = parentContext ? Object.assign({}, parentContext, upperContext) : upperContext;
            const newRows = constructTableContentsFromDataSet(row, repeatKey, modifiedContents, totalRows, rowIndex, upperContext);
            totalRows += newRows.length - 1;
            newRow = newRow.map(r => Object.assign({}, r, { rowSpan: r.rowSpan + newRows.length - 1 }));
            newRow = newRow.concat(...newRows[0]);
            // if (colIndex === contents.length - 1) {
              rows.push(newRow);
              rows = rows.concat(newRows.slice(1));
            // }
          } else { // TODO : Handling empty data
            newRow = newRow.concat(Object.assign({}, newRow[newRow.length - 1], {
              colSpan: contents.slice(colIndex).length,
              content: Object.assign({}, newRow[newRow.length - 1].content, {
                contents: new Array(
                   {
                     contentType: 'PARAGRAPH',
                     style: {
                       lineSpacing: 1.5,
                       fontWeight: [],
                       color: '#000000',
                       fontFamily: "Open Sans",
                       fontSize: 16
                     },
                     content: ' '
                   },
                   {
                     contentType: "LINE_BREAK",
                     content: "\n",
                     style: {
                       lineSpacing: 1,
                       alignment: "center",
                       color: "#000000",
                       fontFamily: "Open Sans",
                       fontSize: 16
                     }
                   })
              })
            }));
            rows.push(newRow);
          }
          }
        } else {
          const modifiedDataSet = parentContext ? Object.assign({}, parentContext, row) : row;
          newRow.push({
            rowSpan: 1,
            rowIndex: currentRowIndex,
            dataSet: modifiedDataSet,
            ...col
          });
        }
      });
      if (!hasNest) {
        rows.push(newRow);
      }
  })
  return rows;
}

export const calculateRowSpans = (contents, dataSet = undefined, tableName = undefined) => {
  const columnLevels = [];
  let tableContents = [];
  let totalRows = 0;
  let levels = [];
  
  // First pass: calculate depth or inherit
  contents.forEach((col, index) => {
    const repeatVar = col.repeatVariable || "";
    let depth;

    if (repeatVar) {
      const splitRepeatVar = repeatVar.split(".");
      depth = splitRepeatVar.length - 1;
      splitRepeatVar.forEach(splitVar => {
        if(!levels.includes(splitVar)) levels.push(splitVar);
      })
    } else {
      depth = index > 0 ? columnLevels[index - 1].depth : 0;
    }

    columnLevels.push({ column: index + 1, depth });
  });

  if (dataSet !== undefined) {
    tableContents = constructTableContentsFromDataSet(dataSet, tableName, contents, totalRows);
  } else {
    // Max depth determines total rows
    const maxDepth = Math.max(...columnLevels.map(c => c.depth), 0);
    totalRows = Math.pow(2, maxDepth);
    // Second pass: assign rowSpan
    columnLevels.forEach((level, index) => {
      level.rowSpan = totalRows / Math.pow(2, level.depth);
    });
  }

  return {
    totalRows,
    columnLevels,
    tableContents,
    levels
  };
}

export const removeWithChildren = (contents, startIndex) => {
  const target = contents[startIndex];
  if (!target) return contents;

  const base = target.repeatVariable;

  if (!base) {
    // no repeatVariable - just remove this one
    return contents.filter((_, idx) => idx !== startIndex);
  }

  // count how many times base appears
  const baseCount = contents.filter(item => item.repeatVariable === base).length;

  if (baseCount > 1) {
    // base appears more than once - remove only the picked index
    return contents.filter((_, idx) => idx !== startIndex);
  }

  // normal case - remove picked + children
  return contents.filter((item, idx) => {
    if (idx === startIndex) return false;
    if (item.repeatVariable?.startsWith(base + ".")) return false;
    return true;
  });
}

export const dynamicTableHasMoreThanTwoLevels = (arr) => {
  const getDepth = (o) =>
    o.type !== "tableObject" && o.type !== 'object'
      ? 0 // don't count non-tableObjects
      : 1 + Math.max(0, ...(o.variables ?? o.content ?? []).map(getDepth));

  const maxDepth = Math.max(0, ...arr.map(getDepth));

  return {
    hasMoreThanTwoLevels: maxDepth > 2,
    isNestedTable: maxDepth > 1 && maxDepth < 3
  };
};

export const handleBusinessLogic = async ({
  methods,
  selectedTemplate,
  setBusinessLogics,
  blockIdsMap,
  flowIdsMap,
  env = false,
}) => {
  const currentStore = methods.get();
  const blBlocks = getAllBlocksInDocument(selectedTemplate.flows);
  const newlyAddedBusinessLogics = [];
  if (selectedTemplate?.businessLogics?.length) {
    if (env) {
      setBusinessLogics(selectedTemplate?.businessLogics);
      const { flowBusinessLogicMap, elementBusinessLogicMap } = buildBusinessLogicMaps(
        selectedTemplate?.businessLogics,
        currentStore,
      );
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]: flowBusinessLogicMap,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: elementBusinessLogicMap,
      });
      return;
    }
    for (let i = 0; i < selectedTemplate.businessLogics.length; i++) {
      const businessLogic = selectedTemplate.businessLogics[i];
      businessLogic.documentId = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
      businessLogic.id = null;
      businessLogic.rules = businessLogic.rules.map(rule => {
        rule.id = null;
        const origUuid = blBlocks.find(oBlock => oBlock.blockId?.includes(rule.blockId))?.uuid;
        if (origUuid) {
          rule.blockId =
            currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][blockIdsMap.get(origUuid)];
        }
        if (rule.flowId && flowIdsMap) {
          const origFlowUuid = selectedTemplate.flows.find(oFlow =>
            oFlow.flowId?.includes(rule.flowId),
          )?.uuid;
          if (origFlowUuid) {
            rule.flowId =
              currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.PERSISTED_KEYS][
                flowIdsMap.get(origFlowUuid)
              ];
          }
        }
        return rule;
      });
      try {
        const response = await updateBusinessLogic(businessLogic);
        if (response?.data) {
          newlyAddedBusinessLogics.push(response.data);
        }
      } catch (e) {
        console.error(e);
      }
    }
    if (newlyAddedBusinessLogics.length) {
      setBusinessLogics(newlyAddedBusinessLogics);
      const { flowBusinessLogicMap, elementBusinessLogicMap } = buildBusinessLogicMaps(
        newlyAddedBusinessLogics,
        currentStore,
      );
      methods.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOW_BUSINESS_LOGIC]: flowBusinessLogicMap,
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.ELEMENT_GROUPS]: elementBusinessLogicMap,
      });
    }
  }
};